/**
 * PAYSTUB DETAILS STEP - ENHANCED WITH REVERSE CALCULATION
 * 
 * Features:
 * - TARGET NET PAY MODE: Enter desired net pay, system calculates everything backwards
 * - TARGET GROSS PAY MODE: Enter desired gross pay, system calculates hours and taxes
 * - Normal mode: Enter hours/salary, system calculates net pay
 * - Tax Intensity Slider (1-10)
 * - ALL earning types: Regular, Overtime (1.5x), Bonus, Commission, Tips, Holiday Pay, Sick Pay, Vacation Pay
 * - ALL fields editable including taxes and dates
 * - Works for both HOURLY and SALARY workers
 * - Year Selection for previous years
 */

import { descendingCheckNumbers, needsCheckRenumber, reserveCheckNumbers, commitCheckNumbers } from '../../lib/checkNumbers';
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Slider } from '../ui/slider';
import { Switch } from '../ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import {
  Calculator,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Edit3,
  Sliders,
  Eye,
  Clock,
  Banknote,
  Target,
  ArrowRight,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Gift,
  Calendar,
  Palmtree,
  Thermometer,
  Umbrella,
  Save,
  X,
  FileText
} from 'lucide-react';
import {
  calculatePayPeriodTaxes,
  STATE_TAX_DATA
} from '../../utils/taxCalculations';
import { buildRealisticYTD, applyYtdOverride, applyFieldYtdOverrides } from '../../utils/ytdAccumulator';
import PriorPeriodsEditor from './PriorPeriodsEditor';

// Available years for selection
const AVAILABLE_YEARS = [2026, 2025, 2024, 2023, 2022];

// Pay period day counts
const PERIOD_DAYS = {
  'weekly': 7,
  'bi-weekly': 14,
  'semi-monthly': 15,
  'monthly': 30
};

// Pay periods per year for each frequency
const PERIODS_PER_YEAR = {
  'weekly': 52,
  'bi-weekly': 26,
  'semi-monthly': 24,
  'monthly': 12
};

// 2026-02-28 — Auto-set the FIRST (most recent) stub's pay date to the
// most recently passed Friday.  If today IS Friday we still use today
// (treat today's Friday as "just passed" — one less click for the admin
// finalizing stubs on payday).  Returns a Date at local noon so any
// subsequent toISOString().split('T')[0] does not drift a day due to TZ.
const getPreviousFriday = (fromDate = new Date()) => {
  const d = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate(), 12, 0, 0);
  const dow = d.getDay(); // 0=Sun … 5=Fri … 6=Sat
  // Days back to the previous (or today's) Friday: Fri→0, Sat→1, Sun→2, Mon→3, Tue→4, Wed→5, Thu→6
  const back = (dow - 5 + 7) % 7;
  d.setDate(d.getDate() - back);
  return d;
};

const formatLocalDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

// Backdate stub i from a base pay date.  i=0 → base date itself; i=1 →
// one period earlier; etc.  Weekly = -7d, bi-weekly = -14d, monthly =
// -1 calendar month (keeps the day-of-month), semi-monthly = -15d.
const backdatePayDate = (baseDate, i, payFrequency) => {
  const d = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate(), 12, 0, 0);
  if (payFrequency === 'monthly') {
    d.setMonth(d.getMonth() - i);
  } else {
    const days = PERIOD_DAYS[payFrequency] || 14;
    d.setDate(d.getDate() - i * days);
  }
  return d;
};

// Calculate stub number in year based on pay date
const calculateStubNumberFromDate = (payDate, payFrequency, year) => {
  if (!payDate) return 1;
  
  const date = new Date(payDate + 'T12:00:00'); // Use noon to avoid timezone issues
  const payYear = date.getFullYear();
  
  // If the pay date is from a different year, calculate relative to that year
  const targetYear = year || payYear;
  const startOfYear = new Date(targetYear, 0, 1); // Jan 1
  
  // Calculate days from start of year
  const daysFromStart = Math.floor((date - startOfYear) / (1000 * 60 * 60 * 24));
  
  // Calculate stub number based on pay frequency
  let stubNum = 1;
  switch (payFrequency) {
    case 'weekly':
      stubNum = Math.ceil((daysFromStart + 1) / 7);
      break;
    case 'bi-weekly':
      stubNum = Math.ceil((daysFromStart + 1) / 14);
      break;
    case 'semi-monthly':
      // Semi-monthly: 2 per month, so roughly day/15 * 2 + month offset
      const month = date.getMonth(); // 0-indexed
      const dayOfMonth = date.getDate();
      stubNum = (month * 2) + (dayOfMonth > 15 ? 2 : 1);
      break;
    case 'monthly':
      stubNum = date.getMonth() + 1; // 1-indexed month
      break;
    default:
      stubNum = Math.ceil((daysFromStart + 1) / 14); // Default to bi-weekly
  }
  
  // Ensure stub number is within valid range
  const maxStubs = PERIODS_PER_YEAR[payFrequency] || 26;
  return Math.max(1, Math.min(stubNum, maxStubs));
};

// Round to two decimal places
const roundToTwo = (num) => Math.round((num + Number.EPSILON) * 100) / 100;

// 2026-02-XX (user request) — compact helper that builds a stub's YTD
// AND applies the user's optional ytdOverride in a single call so the
// 7 existing buildRealisticYTD call-sites don't each have to remember
// to apply the override.
//
// 2026-02-XX (follow-up) — also honours per-field overrides from
// `ytdOverrides` dict (Edit YTD mode).  Order: (1) build jittered YTD,
// (2) rescale by overall grossPay override (legacy), (3) merge in any
// per-field overrides the user typed in Edit YTD mode.
const buildStubYtd = (current, stubNumberInYear, seed, ytdOverride, ytdOverrides) => {
  let built = buildRealisticYTD(current, stubNumberInYear, seed);
  if (ytdOverride && ytdOverride > 0) {
    built = applyYtdOverride(built, current?.grossPay || 0, ytdOverride);
  }
  if (ytdOverrides && typeof ytdOverrides === 'object' && Object.keys(ytdOverrides).length) {
    built = applyFieldYtdOverrides(built, ytdOverrides);
  }
  return built;
};

export default function PaystubDetailsStep({ formData, updateFormData, onSaveClient }) {
  const [expandedStub, setExpandedStub] = useState(0);
  // 2026-02-XX (user bug report) — Pre-filled number inputs used to
  // leave a leading "0" when the user typed on top (so "0" + "2000"
  // became "02000" → paystub showed outrageous amounts).  Fix: on
  // focus, select the whole value so typing replaces instead of
  // prefixing.  One delegated listener on the step root handles every
  // number input without touching all 22+ inputs individually.
  const stepRootRef = useRef(null);
  useEffect(() => {
    const root = stepRootRef.current;
    if (!root) return;
    const handler = (ev) => {
      const t = ev.target;
      if (t && t.tagName === 'INPUT' && t.type === 'number') {
        // defer so the browser's native focus completes first
        setTimeout(() => { try { t.select(); } catch (e) {} }, 0);
      }
    };
    root.addEventListener('focusin', handler);
    return () => root.removeEventListener('focusin', handler);
  }, []);
  const [taxIntensity, setTaxIntensity] = useState(formData.taxIntensity || 3);
  const [targetNetPayMode, setTargetNetPayMode] = useState(formData.targetNetPayMode || false);
  const [targetNetPay, setTargetNetPay] = useState(formData.targetNetPay || '');
  // 2026-02-XX (user request) — Edit YTD mode toggle.  When ON, every
  // YTD cell on every stub becomes an inline input.  2026-02-28 (user
  // request round 2): default this ON because the admin always wants
  // YTD editable; the explicit toggle felt like a hidden gate.
  // Overrides are persisted per-stub in `stub.ytdOverrides[field]`
  // and cascade backwards to prior stubs proportionally.
  const [editYtdMode, setEditYtdMode] = useState(true);
  // Reverse-calc extras picker: lets the user say the target net/gross
  // *includes* overtime / bonus / commission / tips, and specify the
  // dollar amount for each.  Keyed per-stub so each row remembers.
  const [stubExtras, setStubExtras] = useState({});
  const [targetGrossPayMode, setTargetGrossPayMode] = useState(formData.targetGrossPayMode || false);
  const [targetGrossPay, setTargetGrossPay] = useState(formData.targetGrossPay || '');
  // 2026-02-28 (user request) — Auto-populate Target Net/Gross from the
  // employment details so the number flows from Step 4 → Step 5 without
  // retyping.  Only fires when the admin hasn't already set a target.
  // Honours the new `payEntryMode` picker:
  //   • 'annual'   → derive per-period gross from annual salary (or
  //                  hourly × hours) and seed BOTH gross and estimated
  //                  net targets.
  //   • 'per-check'→ seed the TARGET directly from `perCheckAmount` and
  //                  put it into either targetNetPay or targetGrossPay
  //                  based on `perCheckType`.  The other target is left
  //                  blank — only ONE drives the math (user's request).
  useEffect(() => {
    const periodsPerYear = {
      'weekly': 52, 'bi-weekly': 26, 'biweekly': 26,
      'semi-monthly': 24, 'semimonthly': 24, 'monthly': 12,
    };
    const periods = periodsPerYear[formData.payFrequency] || 26;

    if (formData.payEntryMode === 'per-check') {
      const amt = parseFloat(formData.perCheckAmount) || 0;
      if (amt > 0) {
        const isNet = (formData.perCheckType || 'net') === 'net';
        if (isNet && !targetNetPay) setTargetNetPay(String(amt.toFixed(2)));
        if (!isNet && !targetGrossPay) setTargetGrossPay(String(amt.toFixed(2)));
      }
      return;
    }

    // Default 'annual' mode: use salary / hourly to seed both targets.
    let perPeriodGross = 0;
    if (formData.paymentType === 'hourly') {
      const rate = parseFloat(formData.hourlyRate) || 0;
      const hours = parseFloat(formData.hoursPerPeriod) || 0;
      const ot = parseFloat(formData.overtimeHours) || 0;
      perPeriodGross = rate * hours + rate * 1.5 * ot;
    } else {
      const annual = parseFloat(formData.annualSalary) || 0;
      perPeriodGross = annual / periods;
    }
    if (perPeriodGross > 0) {
      if (!targetGrossPay) setTargetGrossPay(String(perPeriodGross.toFixed(2)));
      if (!targetNetPay) {
        const multiplier = 0.5 + ((formData.taxIntensity || 3) - 1) * 0.111;
        const estTaxRate = 0.08 + multiplier * 0.15;
        const estNet = perPeriodGross * (1 - Math.min(0.4, estTaxRate));
        setTargetNetPay(String(estNet.toFixed(2)));
      }
    }
    // Dependencies covered below.
  }, [formData.payEntryMode, formData.perCheckAmount, formData.perCheckType,
      formData.paymentType, formData.annualSalary, formData.hourlyRate,
      formData.hoursPerPeriod, formData.overtimeHours, formData.payFrequency]);
  const [selectedYear, setSelectedYear] = useState(formData.selectedYear || new Date().getFullYear());
  const [stubNumberInYear, setStubNumberInYear] = useState(formData.stubNumberInYear || 1);
  const [includeExtraIncome, setIncludeExtraIncome] = useState(formData.includeExtraIncome !== false);
  const [adjustmentField, setAdjustmentField] = useState(formData.adjustmentField || 'bonus');
  const [stubAdjustmentFields, setStubAdjustmentFields] = useState(formData.stubAdjustmentFields || {});
  // 2026-06-23 — per-stub reverse-calc inputs.  Keyed by stub index so each
  // expanded paystub card carries its own Target Net + Target Gross input
  // without clobbering the global Reverse Calculation card at the top.
  const [stubTargetNet, setStubTargetNet] = useState({});
  const [stubTargetGross, setStubTargetGross] = useState({});
  
  // Track the previous numberOfStubs to detect actual user changes
  const prevNumberOfStubsRef = useRef(null);
  const isGeneratingRef = useRef(false);
  const hasInitializedRef = useRef(false);

  // 2026-02-28 (user request) — Deductions Meter 5-20%.  Slider now
  // represents the TARGET effective deduction rate (deductions / gross)
  // directly as a percentage.  Mapped onto the old 1-10 intensity scale
  // so the existing recalc logic keeps working:
  //     intensity 1  → ~10% effective
  //     intensity 10 → ~35% effective
  //  With getTaxMultiplier = 0.5 + (intensity-1)*0.111,
  //     intensity = 1 + (target% - 10) * 9 / 25
  //  Clamped so 5% → ~0.4 (still legal) and 20% → ~4.6.
  const taxIntensityPct = useMemo(() => {
    // 5-20% target.  Invert getTaxMultiplier via a fit of the real tax
    // tables: multiplier 0.5 ≈ 8%, multiplier 1.5 ≈ 32%.
    // Simpler: approximate target = 8 + (intensity-1) * 2.7
    // → intensity ≈ 1 + (target - 8) / 2.7
    // Clamp to the slider range when read.
    return Math.max(5, Math.min(20, Math.round(8 + (taxIntensity - 1) * 2.7)));
  }, [taxIntensity]);
  const setTaxIntensityPct = useCallback((pct) => {
    const clamped = Math.max(5, Math.min(20, pct));
    const nextIntensity = Math.max(1, Math.min(10, 1 + (clamped - 8) / 2.7));
    setTaxIntensity(Number(nextIntensity.toFixed(2)));
  }, [setTaxIntensity]);

  // Tax intensity multiplier: 1 = 50% of calculated, 10 = 150% of calculated
  const getTaxMultiplier = useCallback(() => {
    return 0.5 + (taxIntensity - 1) * 0.111;
  }, [taxIntensity]);

  // Initialize paystubs on mount OR when numberOfStubs / pay-config changes.
  //
  // 2026-06-23 fix: the previous version had several refs + a setTimeout
  // race that left only the FIRST stub rendered when the user picked 3
  // paystubs from Employment Details and clicked Next.  Replaced with a
  // simple "regen whenever the count doesn't match" pattern that is
  // re-entrancy safe (single ref guard, no timeout).
  useEffect(() => {
    const requested = formData.numberOfStubs || 1;
    const have = formData.paystubs?.length || 0;

    // 2026-02-28 — When only the frequency (or paymentType) changed and
    // the stub count is unchanged, we do NOT rebuild everything from
    // scratch — we just re-space the existing pay dates.  This preserves
    // any manual gross/tax edits the user has already made on lower
    // stubs.  We ONLY recalc dates when stub[0]'s pay date is still the
    // auto-default (== previous Friday), meaning the user hasn't
    // manually edited any date yet.  Once the user edits stub[0] the
    // `autoCalculateAllDates` helper takes over and stub[0].payDate
    // will differ from the auto default, so we skip.
    if (have === requested && have > 0) {
      const autoFridayStr = formatLocalDate(getPreviousFriday());
      const stub0Date = formData.paystubs?.[0]?.payDate;
      if (stub0Date === autoFridayStr && !isGeneratingRef.current) {
        // Dates untouched — safe to re-space using new frequency.
        isGeneratingRef.current = true;
        const days = PERIOD_DAYS[formData.payFrequency] || 14;
        const base = getPreviousFriday();
        const respaced = formData.paystubs.map((s, i) => {
          const pd = backdatePayDate(base, i, formData.payFrequency);
          const pe = new Date(pd); pe.setDate(pe.getDate() - 5);
          const ps = new Date(pe); ps.setDate(ps.getDate() - (days - 1));
          return {
            ...s,
            payDate: formatLocalDate(pd),
            periodEnd: formatLocalDate(pe),
            periodStart: formatLocalDate(ps),
          };
        });
        updateFormData({ paystubs: respaced });
        Promise.resolve().then(() => { isGeneratingRef.current = false; });
      }
      return;
    }

    if (have === requested) return;
    if (isGeneratingRef.current) return;

    isGeneratingRef.current = true;
    hasInitializedRef.current = true;
    console.log(`[PaystubDetails] regen ${have} → ${requested} stubs (freq=${formData.payFrequency})`);

      const stubs = [];
      // 2026-02-28 — First (most recent) stub's pay date = the most
      // recently passed Friday (or today if today is Friday).  Every
      // subsequent stub steps BACKWARD in time by one pay period.
      // Weekly = -7d, bi-weekly = -14d, semi-monthly = -15d, monthly =
      // -1 calendar month.  This replaces the old forward-from-today
      // logic that generated future-dated stubs.
      const firstPayDate = getPreviousFriday();
      const days = PERIOD_DAYS[formData.payFrequency] || 14;
    const requestedStubCount = requested;
      
      // Newest stub (index 0) gets the highest check #; older ones 30-80 lower.
      const checkNumbers = descendingCheckNumbers(requestedStubCount);

      let baseRegular = 0;
      let baseOvertime = 0;
      let baseHours = 0;
      let baseOvertimeHours = 0;
      let baseRate = 0;

      if (formData.paymentType === 'hourly') {
        baseRate = formData.hourlyRate || 20;
        baseHours = formData.hoursPerPeriod || 80;
        baseOvertimeHours = formData.overtimeHours > 0 ? formData.overtimeHours : 0;
        baseRegular = baseRate * baseHours;
        baseOvertime = baseOvertimeHours > 0 ? baseRate * 1.5 * baseOvertimeHours : 0;
      } else {
        const periodsPerYear = { 'weekly': 52, 'bi-weekly': 26, 'semi-monthly': 24, 'monthly': 12 };
        baseRegular = (formData.annualSalary || 52000) / (periodsPerYear[formData.payFrequency] || 26);
        baseOvertimeHours = 0;
        baseOvertime = 0;
      }

      const baseGross = baseRegular + baseOvertime;
      const multiplier = getTaxMultiplier();

      // Generate all stubs based on requestedStubCount.  i=0 is the
      // MOST RECENT stub (pay date = previous Friday); i grows =
      // older stubs.
      for (let i = 0; i < requestedStubCount; i++) {
        // Backward-from-Friday date math (see helpers at file top).
        const payDate = backdatePayDate(firstPayDate, i, formData.payFrequency);
        const periodEnd = new Date(payDate);
        periodEnd.setDate(periodEnd.getDate() - 5);
        const periodStart = new Date(periodEnd);
        periodStart.setDate(periodStart.getDate() - (days - 1));

        let stubHours = baseHours;
        let stubRegular = baseRegular;
        let stubGross = baseGross;

        if (formData.paymentType === 'hourly' && formData.usePerStubHours && formData.perStubHours && formData.perStubHours[i] !== undefined) {
          stubHours = formData.perStubHours[i];
          stubRegular = roundToTwo(baseRate * stubHours);
          stubGross = roundToTwo(stubRegular + baseOvertime);
        }

        const current = {
          regular: roundToTwo(stubRegular),
          overtime: roundToTwo(baseOvertime),
          bonus: 0,
          commission: 0,
          tips: 0,
          holidayPay: 0,
          sickPay: 0,
          vacationPay: 0,
          hours: stubHours,
          overtimeHours: baseOvertimeHours,
          rate: baseRate,
          grossPay: roundToTwo(stubGross),
          federalTax: 0,
          stateTax: 0,
          socialSecurity: 0,
          medicare: 0,
          sdi: 0,
          totalTaxes: 0,
          deductions: 0,
          totalDeductions: 0,
          netPay: 0
        };

        // Calculate taxes
        const baseTaxes = calculatePayPeriodTaxes(
          current.grossPay,
          formData.payFrequency,
          formData.taxState,
          formData.maritalStatus || 'single',
          formData.exemptions || 1,
          current.grossPay * i
        );

        current.federalTax = roundToTwo(baseTaxes.federalTax * multiplier);
        current.stateTax = roundToTwo(baseTaxes.stateTax * multiplier);
        current.socialSecurity = roundToTwo(baseTaxes.socialSecurity);
        current.medicare = roundToTwo(baseTaxes.medicare);
        current.sdi = roundToTwo(baseTaxes.sdi * multiplier);

        current.totalTaxes = roundToTwo(
          current.federalTax + current.stateTax + current.socialSecurity + current.medicare + current.sdi
        );
        current.totalDeductions = current.totalTaxes + (current.deductions || 0);
        current.netPay = roundToTwo(current.grossPay - current.totalDeductions);

        const actualStubNum = stubNumberInYear - i;
        const ytd = buildRealisticYTD(
          current,
          actualStubNum,
          `${formData.employeeName || 'emp'}|${formData.employeeSSN || ''}|${actualStubNum}`,
        );

        stubs.push({
          stubNumber: actualStubNum,
          stubNumberInYear: actualStubNum,
          checkNumber: checkNumbers[i],
          periodStart: formatLocalDate(periodStart),
          periodEnd: formatLocalDate(periodEnd),
          payDate: formatLocalDate(payDate),
          current,
          ytd
        });
      }

    console.log(`[PaystubDetails] regen produced ${stubs.length} stubs`);
      updateFormData({ paystubs: stubs, checkNumbersV2: false });

    // Flip the in-flight guard off after React has committed.  Using
    // microtask instead of setTimeout makes this synchronous from the
    // user's POV and prevents the previous race where re-entrant effects
    // could collapse the array back to length 1.
    Promise.resolve().then(() => {
        isGeneratingRef.current = false;
    });

    if (have > 0) {
      toast.success(`Updated to ${requestedStubCount} paystub${requestedStubCount > 1 ? 's' : ''}`);
      }
    
    prevNumberOfStubsRef.current = requestedStubCount;
  }, [formData.numberOfStubs, formData.paystubs?.length, formData.payFrequency, formData.paymentType]);

  // Saved stubs keep their amounts when you come back to this page; taxes only recalc when the
  // intensity actually changes (or stubs arrive without taxes, e.g. intake imports).
  const stubsLackTaxes = (formData.paystubs || []).some(
    (st) => Number(st.current?.grossPay) > 0 && !(Number(st.current?.federalTax) || Number(st.current?.socialSecurity) || Number(st.current?.medicare))
  );
  const preservedIntensityRef = useRef(stubsLackTaxes && !formData.needsTaxCalc ? null : taxIntensity);

  // Recalculate when tax intensity changes
  useEffect(() => {
    if (preservedIntensityRef.current === taxIntensity) {
      // keep uploaded / re-dated amounts exactly
    } else if (formData.paystubs?.length > 0 && !targetNetPayMode) {
      preservedIntensityRef.current = null;
      recalculateAllTaxes();
    }
    updateFormData({ taxIntensity });
  }, [taxIntensity]);

  // Update stubNumberInYear changes
  useEffect(() => {
    updateFormData({ stubNumberInYear });
  }, [stubNumberInYear]);

  // AUTO-CALCULATE stub number when the first pay date is set
  const preservedPayDateRef = useRef(formData.stubNumberInYear ? formData.paystubs?.[0]?.payDate : null);
  useEffect(() => {
    if (preservedPayDateRef.current && preservedPayDateRef.current === formData.paystubs?.[0]?.payDate) return;
    preservedPayDateRef.current = null;
    if (formData.paystubs?.[0]?.payDate && formData.payFrequency) {
      const autoStubNum = calculateStubNumberFromDate(
        formData.paystubs[0].payDate,
        formData.payFrequency,
        selectedYear
      );
      if (autoStubNum !== stubNumberInYear) {
        setStubNumberInYear(autoStubNum);
      }
    }
  }, [formData.paystubs?.[0]?.payDate, formData.payFrequency, selectedYear]);

  // Recalculate YTD when stubNumberInYear changes (not just because the page re-opened)
  const stubsHaveYtd = (formData.paystubs || []).length > 0 &&
    (formData.paystubs || []).every((st) => Number(st.ytd?.grossPay) >= Number(st.current?.grossPay || 0) && Number(st.ytd?.grossPay) > 0);
  const preservedStubNumRef = useRef(stubsHaveYtd ? stubNumberInYear : null);
  useEffect(() => {
    if (preservedStubNumRef.current === stubNumberInYear) return;
    preservedStubNumRef.current = null;
    if (formData.paystubs?.length > 0) {
      recalculateYTDForStubNumber();
    }
  }, [stubNumberInYear]);

  const generateCheckNumbers = (count) => descendingCheckNumbers(count);

  // Client-intake imports: compute real taxes/YTD once from the client's per-check gross or net.
  useEffect(() => {
    if (!formData.needsTaxCalc || !formData.paystubs?.length) return;
    if (formData.paystubs.length !== Number(formData.numberOfStubs || formData.paystubs.length)) return;
    const amt = Number(formData.perCheckAmount) || 0;
    if (amt > 0 && formData.perCheckType === 'net') calculateFromTargetNetPay(amt);
    else if (amt > 0) calculateFromTargetGrossPay(amt);
    else recalculateAllTaxes();
    updateFormData({ needsTaxCalc: false });
  }, [formData.needsTaxCalc, formData.paystubs?.length]);

  // Fresh stubs (flag false) and legacy short/out-of-order numbers pull unique numbers from the
  // company's counter; legacy valid numbers just raise the counter. Runs once per stub set.
  const reservingRef = useRef(false);
  useEffect(() => {
    const stubs = formData.paystubs || [];
    if (formData.checkNumbersV2 === true || !stubs.length || reservingRef.current) return;
    const company = (formData.companyName || '').trim();
    const mustRenumber = formData.checkNumbersV2 === false || needsCheckRenumber(stubs);
    const applyNums = (nums) => updateFormData(prev => ({
      checkNumbersV2: true,
      paystubs: (prev.paystubs || []).map((st, i) => (nums[i] ? { ...st, checkNumber: nums[i] } : st)),
    }));
    if (!mustRenumber) {
      if (company) commitCheckNumbers(company, stubs.map(st => st.checkNumber)).catch(() => {});
      updateFormData({ checkNumbersV2: true });
      return;
    }
    if (!company) { applyNums(descendingCheckNumbers(stubs.length)); return; }
    reservingRef.current = true;
    reserveCheckNumbers(company, stubs.length)
      .then(applyNums)
      .catch(() => applyNums(descendingCheckNumbers(stubs.length)))
      .finally(() => { reservingRef.current = false; });
  }, [formData.paystubs?.length, formData.checkNumbersV2]);

  const generateInitialPaystubs = () => {
    const stubs = [];
    // 2026-02-28 — Same backward-from-Friday model as the main init
    // effect: stub 0 = previous Friday, older stubs step back by one
    // pay period each (calendar month for monthly).
    const firstPayDate = getPreviousFriday();
    const days = PERIOD_DAYS[formData.payFrequency] || 14;
    const checkNumbers = generateCheckNumbers(formData.numberOfStubs);

    let baseRegular = 0;
    let baseOvertime = 0;
    let baseHours = 0;
    let baseOvertimeHours = 0;
    let baseRate = 0;

    if (formData.paymentType === 'hourly') {
      baseRate = formData.hourlyRate || 20;
      baseHours = formData.hoursPerPeriod || 80;
      baseOvertimeHours = formData.overtimeHours > 0 ? formData.overtimeHours : 0;
      baseRegular = baseRate * baseHours;
      baseOvertime = baseOvertimeHours > 0 ? baseRate * 1.5 * baseOvertimeHours : 0;
    } else {
      const periodsPerYear = { 'weekly': 52, 'bi-weekly': 26, 'semi-monthly': 24, 'monthly': 12 };
      baseRegular = (formData.annualSalary || 52000) / (periodsPerYear[formData.payFrequency] || 26);
      baseOvertimeHours = 0;
      baseOvertime = 0;
    }

    const baseGross = baseRegular + baseOvertime;

    for (let i = 0; i < formData.numberOfStubs; i++) {
      const payDate = backdatePayDate(firstPayDate, i, formData.payFrequency);
      const periodEnd = new Date(payDate);
      periodEnd.setDate(periodEnd.getDate() - 5);
      const periodStart = new Date(periodEnd);
      periodStart.setDate(periodStart.getDate() - (days - 1));

      let stubHours = baseHours;
      let stubRegular = baseRegular;
      let stubGross = baseGross;

      if (formData.paymentType === 'hourly' && formData.usePerStubHours && formData.perStubHours && formData.perStubHours[i] !== undefined) {
        stubHours = formData.perStubHours[i];
        stubRegular = roundToTwo(baseRate * stubHours);
        stubGross = roundToTwo(stubRegular + baseOvertime);
      }

      const current = {
        regular: roundToTwo(stubRegular),
        overtime: roundToTwo(baseOvertime),
        bonus: 0,
        commission: 0,
        tips: 0,
        holidayPay: 0,
        sickPay: 0,
        vacationPay: 0,
        hours: stubHours,
        overtimeHours: baseOvertimeHours,
        rate: baseRate,
        grossPay: roundToTwo(stubGross),
        federalTax: 0,
        stateTax: 0,
        socialSecurity: 0,
        medicare: 0,
        sdi: 0,
        totalTaxes: 0,
        deductions: 0,
        totalDeductions: 0,
        netPay: 0
      };

      // Calculate taxes
      const multiplier = getTaxMultiplier();
      const baseTaxes = calculatePayPeriodTaxes(
        current.grossPay,
        formData.payFrequency,
        formData.taxState,
        formData.maritalStatus || 'single',
        formData.exemptions || 1,
        current.grossPay * i
      );

      current.federalTax = roundToTwo(baseTaxes.federalTax * multiplier);
      current.stateTax = roundToTwo(baseTaxes.stateTax * multiplier);
      current.socialSecurity = roundToTwo(baseTaxes.socialSecurity);
      current.medicare = roundToTwo(baseTaxes.medicare);
      current.sdi = roundToTwo(baseTaxes.sdi * multiplier);

      current.totalTaxes = roundToTwo(
        current.federalTax + current.stateTax + current.socialSecurity + current.medicare + current.sdi
      );
      current.totalDeductions = current.totalTaxes + (current.deductions || 0);
      current.netPay = roundToTwo(current.grossPay - current.totalDeductions);

      const actualStubNum = stubNumberInYear - i;
      const ytd = buildRealisticYTD(
        current,
        actualStubNum,
        `${formData.employeeName || 'emp'}|${formData.employeeSSN || ''}|${actualStubNum}`,
      );

      stubs.push({
        stubNumber: actualStubNum,
        stubNumberInYear: actualStubNum,
        checkNumber: checkNumbers[i],
        periodStart: formatLocalDate(periodStart),
        periodEnd: formatLocalDate(periodEnd),
        payDate: formatLocalDate(payDate),
        current,
        ytd
      });
    }

    updateFormData({ paystubs: stubs, checkNumbersV2: false });
  };

  const recalculateAllTaxes = () => {
    // 2026-06-23 — same closure-safety pattern as recalculateYTDForStubNumber.
    const multiplier = getTaxMultiplier();
    updateFormData(prev => {
      if (!prev.paystubs || prev.paystubs.length === 0) return {};
      const newStubs = prev.paystubs.map((stub, index) => {
        const current = { ...stub.current };

        current.grossPay = roundToTwo(
          (current.regular || 0) +
          (current.overtime || 0) +
          (current.bonus || 0) +
          (current.commission || 0) +
          (current.tips || 0) +
          (current.holidayPay || 0) +
          (current.sickPay || 0) +
          (current.vacationPay || 0)
        );

        const baseTaxes = calculatePayPeriodTaxes(
          current.grossPay,
          prev.payFrequency,
          prev.taxState,
          prev.maritalStatus || 'single',
          prev.exemptions || 1,
          current.grossPay * index
        );

        current.federalTax = roundToTwo(baseTaxes.federalTax * multiplier);
        current.stateTax = roundToTwo(baseTaxes.stateTax * multiplier);
        current.socialSecurity = roundToTwo(baseTaxes.socialSecurity);
        current.medicare = roundToTwo(baseTaxes.medicare);
        current.sdi = roundToTwo(baseTaxes.sdi * multiplier);

        current.totalTaxes = roundToTwo(
          current.federalTax + current.stateTax + current.socialSecurity + current.medicare + current.sdi
        );
        current.totalDeductions = current.totalTaxes + (current.deductions || 0);
        current.netPay = roundToTwo(current.grossPay - current.totalDeductions);

        const actualStubNum = stubNumberInYear - index;
        const ytd = buildStubYtd(
          current,
          actualStubNum,
          `${prev.employeeName || 'emp'}|${prev.employeeSSN || ''}|${actualStubNum}|tax`,
          stub.ytdOverride,
          stub.ytdOverrides,
        );

        return { ...stub, current, ytd, taxOverrides: {}, stubNumber: actualStubNum, stubNumberInYear: actualStubNum };
      });
      return { paystubs: newStubs };
    });
  };

  const recalculateYTDForStubNumber = () => {
    // 2026-06-23 — use functional form so we always operate on the
    // freshest paystubs array.  Previous version read formData.paystubs
    // from closure and, when this effect fired in the same render cycle
    // as the multi-stub regeneration, was clobbering the new array back
    // to its previous (typically 1-stub) length.
    updateFormData(prev => {
      if (!prev.paystubs || prev.paystubs.length === 0) return {};
      const newStubs = prev.paystubs.map((stub, index) => {
        const actualStubNum = stubNumberInYear - index;
        const current = stub.current;
        const ytd = buildStubYtd(
          current,
          actualStubNum,
          `${prev.employeeName || 'emp'}|${prev.employeeSSN || ''}|${actualStubNum}`,
          stub.ytdOverride,
          stub.ytdOverrides,
        );
        return { ...stub, ytd, stubNumber: actualStubNum, stubNumberInYear: actualStubNum };
      });
      return { paystubs: newStubs };
    });
  };

  // REVERSE CALCULATION: Calculate gross pay from target net pay
  // 2026-02-XX (user request) — optional `extras` dict lets the caller
  // declare that the target amount INCLUDES specific extras (overtime
  // / bonus / commission / tips).  When provided, we set those fields
  // to the user-typed amounts and back out regular pay from the
  // remainder, instead of lumping everything into a single
  // `adjustmentField` line.
  const calculateFromTargetNetPay = (targetNet, stubIndex = null, extras = null) => {
    if (!targetNet || targetNet <= 0) {
      toast.error('Please enter a valid Target Net Pay amount');
      return;
    }

    const multiplier = getTaxMultiplier();
    let estimatedGross = targetNet * 1.25;

    // Iterative refinement
    for (let i = 0; i < 5; i++) {
      const baseTaxes = calculatePayPeriodTaxes(
        estimatedGross,
        formData.payFrequency,
        formData.taxState,
        formData.maritalStatus || 'single',
        formData.exemptions || 1,
        0
      );

      const federalTax = roundToTwo(baseTaxes.federalTax * multiplier);
      const stateTax = roundToTwo(baseTaxes.stateTax * multiplier);
      const socialSecurity = roundToTwo(baseTaxes.socialSecurity);
      const medicare = roundToTwo(baseTaxes.medicare);
      const sdi = roundToTwo(baseTaxes.sdi * multiplier);

      const totalTaxes = federalTax + stateTax + socialSecurity + medicare + sdi;
      const calculatedNet = estimatedGross - totalTaxes;

      const difference = targetNet - calculatedNet;
      estimatedGross = roundToTwo(estimatedGross + difference);
    }

    const finalGrossRaw = roundToTwo(estimatedGross);
    const baseTaxes = calculatePayPeriodTaxes(
      finalGrossRaw,
      formData.payFrequency,
      formData.taxState,
      formData.maritalStatus || 'single',
      formData.exemptions || 1,
      0
    );

    const federalTax = roundToTwo(baseTaxes.federalTax * multiplier);
    const stateTax = roundToTwo(baseTaxes.stateTax * multiplier);
    const socialSecurity = roundToTwo(baseTaxes.socialSecurity);
    const medicare = roundToTwo(baseTaxes.medicare);
    const sdi = roundToTwo(baseTaxes.sdi * multiplier);
    const totalTaxes = roundToTwo(federalTax + stateTax + socialSecurity + medicare + sdi);

    // 2026-02-27 — Cent-exact reconciliation.  Each tax component above is
    // independently rounded to 2 decimals, so finalGrossRaw - totalTaxes can
    // drift a few cents away from targetNet.  When the PDF generator does
    // its own `gross - sum(deductions)` math in extract_data, it sees the
    // drift and overrides the stored netPay with the computed value (e.g.
    // user types 4636.27, PDF shows 4636.02).  Fix: force the stored gross
    // to be exactly targetNet + totalTaxes so the identity holds on the
    // printed paystub down to the penny.
    const finalGross = roundToTwo(targetNet + totalTaxes);

    let regularPay = 0;
    let adjustmentAmount = 0;
    let hours = 0;
    let rate = formData.hourlyRate || 20;

    // 2026-02-XX — Extras mode: user said the target INCLUDES these
    // specific line items.  Allocate each extra to its own field and
    // back out regular pay from the remainder.
    const extrasActive = extras && typeof extras === 'object' &&
      ['overtime','bonus','commission','tips'].some(k => Number(extras[k]) > 0);
    const extraVals = {
      overtime: extrasActive ? roundToTwo(Number(extras?.overtime) || 0) : 0,
      bonus: extrasActive ? roundToTwo(Number(extras?.bonus) || 0) : 0,
      commission: extrasActive ? roundToTwo(Number(extras?.commission) || 0) : 0,
      tips: extrasActive ? roundToTwo(Number(extras?.tips) || 0) : 0,
    };
    const extrasSum = extraVals.overtime + extraVals.bonus + extraVals.commission + extraVals.tips;

    if (formData.paymentType === 'hourly') {
      const baseHours = formData.hoursPerPeriod || 80;
      regularPay = roundToTwo(baseHours * rate);
      hours = baseHours;

      if (extrasActive) {
        regularPay = roundToTwo(finalGross - extrasSum);
        if (regularPay < 0) regularPay = 0;
        hours = rate > 0 ? roundToTwo(regularPay / rate) : baseHours;
      } else if (finalGross > regularPay) {
        adjustmentAmount = roundToTwo(finalGross - regularPay);
      } else {
        hours = roundToTwo(finalGross / rate);
        regularPay = roundToTwo(hours * rate);
      }
    } else {
      const periodsPerYear = { 'weekly': 52, 'bi-weekly': 26, 'semi-monthly': 24, 'monthly': 12 };
      regularPay = roundToTwo((formData.annualSalary || 52000) / (periodsPerYear[formData.payFrequency] || 26));

      if (extrasActive) {
        regularPay = roundToTwo(finalGross - extrasSum);
        if (regularPay < 0) regularPay = 0;
      } else if (finalGross > regularPay) {
        adjustmentAmount = roundToTwo(finalGross - regularPay);
      } else {
        regularPay = finalGross;
      }
    }

    const newStubs = formData.paystubs.map((stub, index) => {
      if (stubIndex !== null && index !== stubIndex) {
        return stub;
      }

      const actualStubNum = stubNumberInYear - index;
      const stubAdjField = stubAdjustmentFields[index] || adjustmentField;

      const current = {
        regular: regularPay,
        overtime: extrasActive ? extraVals.overtime : 0,
        bonus: extrasActive ? extraVals.bonus : (stubAdjField === 'bonus' ? adjustmentAmount : 0),
        commission: extrasActive ? extraVals.commission : (stubAdjField === 'commission' ? adjustmentAmount : 0),
        tips: extrasActive ? extraVals.tips : (stubAdjField === 'tips' ? adjustmentAmount : 0),
        holidayPay: extrasActive ? 0 : (stubAdjField === 'holidayPay' ? adjustmentAmount : 0),
        sickPay: extrasActive ? 0 : (stubAdjField === 'sickPay' ? adjustmentAmount : 0),
        vacationPay: extrasActive ? 0 : (stubAdjField === 'vacationPay' ? adjustmentAmount : 0),
        hours: hours,
        overtimeHours: 0,
        rate: rate,
        grossPay: finalGross,
        federalTax,
        stateTax,
        socialSecurity,
        medicare,
        sdi,
        totalTaxes,
        totalDeductions: totalTaxes,
        netPay: roundToTwo(targetNet)
      };

      const ytd = buildRealisticYTD(
        current,
        actualStubNum,
        `${formData.employeeName || 'emp'}|${formData.employeeSSN || ''}|${actualStubNum}|target`,
      );

      return { ...stub, current, ytd, stubNumberInYear: actualStubNum };
    });

    updateFormData({ paystubs: newStubs, targetNetPay: targetNet });

    const fieldNames = {
      'bonus': 'Bonus',
      'commission': 'Commission',
      'tips': 'Tips',
      'holidayPay': 'Holiday Pay',
      'sickPay': 'Sick Pay',
      'vacationPay': 'Vacation Pay'
    };
    const fieldName = fieldNames[stubAdjustmentFields[stubIndex ?? 0] || adjustmentField] || 'Bonus';
    if (adjustmentAmount > 0) {
      toast.success(`Calculated! Regular: $${regularPay.toLocaleString()} + ${fieldName}: $${adjustmentAmount.toLocaleString()} → Net: $${targetNet.toLocaleString()}`);
    } else {
      toast.success(`Calculated! Gross Pay: $${finalGross.toLocaleString()} → Net Pay: $${targetNet.toLocaleString()}`);
    }
  };

  // Calculate from Target Gross Pay
  const calculateFromTargetGrossPay = (targetGross, stubIndex = null, extras = null) => {
    if (!targetGross || targetGross <= 0) {
      toast.error('Please enter a valid Target Gross Pay amount');
      return;
    }

    const multiplier = getTaxMultiplier();

    let calculatedHours = 0;
    let regularPay = targetGross;

    // 2026-02-XX — Extras mode: user says the target gross INCLUDES
    // these extras; back out regular pay as (target − sum(extras)).
    const extrasActive = extras && typeof extras === 'object' &&
      ['overtime','bonus','commission','tips'].some(k => Number(extras[k]) > 0);
    const extraVals = {
      overtime: extrasActive ? roundToTwo(Number(extras?.overtime) || 0) : 0,
      bonus: extrasActive ? roundToTwo(Number(extras?.bonus) || 0) : 0,
      commission: extrasActive ? roundToTwo(Number(extras?.commission) || 0) : 0,
      tips: extrasActive ? roundToTwo(Number(extras?.tips) || 0) : 0,
    };
    const extrasSum = extraVals.overtime + extraVals.bonus + extraVals.commission + extraVals.tips;

    if (extrasActive) {
      regularPay = roundToTwo(Math.max(0, targetGross - extrasSum));
      if (formData.paymentType === 'hourly') {
        const rate = formData.hourlyRate || 20;
        calculatedHours = rate > 0 ? roundToTwo(regularPay / rate) : 0;
      }
    } else if (formData.paymentType === 'hourly') {
      const rate = formData.hourlyRate || 20;
      calculatedHours = roundToTwo(targetGross / rate);
      regularPay = roundToTwo(rate * calculatedHours);
    }

    const baseTaxes = calculatePayPeriodTaxes(
      targetGross,
      formData.payFrequency,
      formData.taxState,
      formData.maritalStatus || 'single',
      formData.exemptions || 1,
      0
    );

    const federalTax = roundToTwo(baseTaxes.federalTax * multiplier);
    const stateTax = roundToTwo(baseTaxes.stateTax * multiplier);
    const socialSecurity = roundToTwo(baseTaxes.socialSecurity);
    const medicare = roundToTwo(baseTaxes.medicare);
    const sdi = roundToTwo(baseTaxes.sdi * multiplier);
    const totalTaxes = roundToTwo(federalTax + stateTax + socialSecurity + medicare + sdi);
    const netPay = roundToTwo(targetGross - totalTaxes);
    const totalDeductions = totalTaxes;

    const updatedPaystubs = formData.paystubs.map((stub, index) => {
      if (stubIndex !== null && index !== stubIndex) return stub;

      const updatedCurrent = {
        ...stub.current,
        regular: regularPay,
        overtime: extrasActive ? extraVals.overtime : (stub.current?.overtime || 0),
        bonus: extrasActive ? extraVals.bonus : (stub.current?.bonus || 0),
        commission: extrasActive ? extraVals.commission : (stub.current?.commission || 0),
        tips: extrasActive ? extraVals.tips : (stub.current?.tips || 0),
        hours: calculatedHours || stub.current.hours,
        grossPay: targetGross,
        federalTax,
        stateTax,
        socialSecurity,
        medicare,
        sdi,
        totalTaxes,
        totalDeductions,
        netPay
      };

      const actualStubNum = stubNumberInYear - index;
      const updatedYTD = buildRealisticYTD(
        updatedCurrent,
        actualStubNum,
        `${formData.employeeName || 'emp'}|${formData.employeeSSN || ''}|${actualStubNum}|gross`,
      );

      return {
        ...stub,
        current: updatedCurrent,
        ytd: updatedYTD,
        stubNumber: actualStubNum,
        stubNumberInYear: actualStubNum
      };
    });

    updateFormData({
      paystubs: updatedPaystubs,
      targetGrossPayMode: true,
      targetGrossPay: targetGross
    });

    if (formData.paymentType === 'hourly') {
      toast.success(`Calculated! Hours: ${calculatedHours} → Gross: $${targetGross.toLocaleString()} → Net: $${netPay.toLocaleString()}`);
    } else {
      toast.success(`Calculated! Gross: $${targetGross.toLocaleString()} → Net: $${netPay.toLocaleString()}`);
    }
  };

  // 2026-02-XX (user request "editable YTD with back-propagation") —
  // When the user types an explicit YTD gross for stub N (because an
  // added bonus / overtime caused the auto-jitter to balloon the
  // number), we:
  //   1. Save N's override and rebuild N's YTD so the printed stub
  //      shows the exact value the user typed.
  //   2. Back-propagate to stub N-1: its new override = N.override −
  //      N.current.grossPay.  Then N-2 chains from N-1 the same way,
  //      etc.
  //   3. Rebuild every affected stub's YTD block so the preview / PDF
  //      / live mini-preview all reflect the corrected values.
  // Clearing the input removes the override chain and falls back to
  // the automatic jittered calculation.
  const updateYtdOverride = (stubIndex, newTargetYTD) => {
    const target = Number(newTargetYTD) || 0;
    const newStubs = [...formData.paystubs];
    if (!newStubs[stubIndex]) return;
    const stubNumberInYear = parseInt(formData.stubNumberInYear) || 1;
    const seed = (n, tag = '') =>
      `${formData.employeeName || 'emp'}|${formData.employeeSSN || ''}|${n}|${tag}`;
    newStubs[stubIndex].ytdOverride = target > 0 ? target : null;
    const nStubNum = stubNumberInYear - stubIndex;
    newStubs[stubIndex].ytd = buildStubYtd(
      newStubs[stubIndex].current,
      nStubNum,
      seed(nStubNum, 'override'),
      newStubs[stubIndex].ytdOverride,
      newStubs[stubIndex].ytdOverrides,
    );
    if (target > 0) {
      let runningTarget = target;
      for (let i = stubIndex; i < newStubs.length - 1; i++) {
        const curGross = newStubs[i].current?.grossPay || 0;
        const priorTarget = roundToTwo(runningTarget - curGross);
        if (priorTarget <= 0) break;
        newStubs[i + 1].ytdOverride = priorTarget;
        const priorStubNum = stubNumberInYear - (i + 1);
        newStubs[i + 1].ytd = buildStubYtd(
          newStubs[i + 1].current,
          priorStubNum,
          seed(priorStubNum, 'bp'),
          priorTarget,
          newStubs[i + 1].ytdOverrides,
        );
        runningTarget = priorTarget;
      }
    } else {
      for (let i = stubIndex + 1; i < newStubs.length; i++) {
        if (newStubs[i].ytdOverride) {
          newStubs[i].ytdOverride = null;
          const sNum = stubNumberInYear - i;
          newStubs[i].ytd = buildStubYtd(
            newStubs[i].current, sNum, seed(sNum, 'clear'), null,
            newStubs[i].ytdOverrides,
          );
        }
      }
    }
    updateFormData({ paystubs: newStubs });
  };

  // 2026-02-XX (user request "Everything editable") — Per-field YTD
  // override.  User flips on Edit YTD mode and types a new value into
  // any YTD cell (Regular / Overtime / Bonus / Commission / Tips /
  // Federal / State / SS / Medicare / SDI / Gross / Net).  We:
  //   1. Save the override on this stub's ytdOverrides dict
  //   2. Rebuild this stub's ytd block honouring the override + any
  //      other overrides already on the stub
  //   3. Recompute the CURRENT value for the same field on this stub
  //      from the delta (new YTD − next-older-stub's YTD for that field)
  //      — so the per-stub number reflects what the user is saying
  //      about the YTD column.
  //   4. Cascade backwards: for each older stub, scale that line's YTD
  //      proportionally so the YTD chain still ties out.
  //   5. Clear the override by passing empty/0 — reverts to auto-calc.
  const CASCADE_FIELDS = new Set([
    'regular','overtime','bonus','commission','tips',
    'holidayPay','sickPay','vacationPay',
    'federalTax','stateTax','socialSecurity','medicare','sdi',
    'grossPay','totalTaxes','netPay',
  ]);
  const CURRENT_FIELD_MAP = {
    regular: 'regular', overtime: 'overtime', bonus: 'bonus',
    commission: 'commission', tips: 'tips',
    holidayPay: 'holidayPay', sickPay: 'sickPay', vacationPay: 'vacationPay',
    federalTax: 'federalTax', stateTax: 'stateTax',
    socialSecurity: 'socialSecurity', medicare: 'medicare', sdi: 'sdi',
    grossPay: 'grossPay', totalTaxes: 'totalTaxes', netPay: 'netPay',
  };

  const updateYtdFieldOverride = (stubIndex, field, rawValue) => {
    if (!CASCADE_FIELDS.has(field)) return;
    const newStubs = [...formData.paystubs];
    if (!newStubs[stubIndex]) return;
    const stubNumberInYear = parseInt(formData.stubNumberInYear) || 1;
    const seed = (n, tag = '') =>
      `${formData.employeeName || 'emp'}|${formData.employeeSSN || ''}|${n}|${tag}`;

    const parsed = rawValue === '' || rawValue === null || rawValue === undefined
      ? null
      : Number(rawValue);
    const clearing = parsed === null || !Number.isFinite(parsed) || parsed < 0;

    // 1. Save override on this stub
    const overrides = { ...(newStubs[stubIndex].ytdOverrides || {}) };
    if (clearing) {
      delete overrides[field];
    } else {
      overrides[field] = roundToTwo(parsed);
    }
    newStubs[stubIndex].ytdOverrides = overrides;

    // 2. Rebuild this stub's YTD
    const thisStubNum = stubNumberInYear - stubIndex;
    newStubs[stubIndex].ytd = buildStubYtd(
      newStubs[stubIndex].current,
      thisStubNum,
      seed(thisStubNum, 'field-override'),
      newStubs[stubIndex].ytdOverride,
      newStubs[stubIndex].ytdOverrides,
    );

    // 3. Recompute CURRENT value for the same field from the delta,
    //    so the per-period number reflects the edited YTD.  (User
    //    opted in to "recompute Current from the delta".)  Only when
    //    the override is a real number, not a clear.
    if (!clearing) {
      const priorStub = newStubs[stubIndex + 1]; // next-older stub
      const priorYtdVal = priorStub?.ytd?.[field] || 0;
      const newCurrent = roundToTwo((overrides[field] || 0) - priorYtdVal);
      const currField = CURRENT_FIELD_MAP[field];
      if (currField && newStubs[stubIndex].current && newCurrent >= 0) {
        newStubs[stubIndex].current[currField] = newCurrent;
        // Re-derive gross/net on CURRENT (same math as updateStubField)
        const c = newStubs[stubIndex].current;
        if (['regular','overtime','bonus','commission','tips','holidayPay','sickPay','vacationPay'].includes(field)) {
          c.grossPay = roundToTwo(
            (c.regular || 0) + (c.overtime || 0) + (c.bonus || 0) +
            (c.commission || 0) + (c.tips || 0) + (c.holidayPay || 0) +
            (c.sickPay || 0) + (c.vacationPay || 0)
          );
        }
        if (['federalTax','stateTax','socialSecurity','medicare','sdi'].includes(field)) {
          c.totalTaxes = roundToTwo(
            (c.federalTax || 0) + (c.stateTax || 0) + (c.socialSecurity || 0) +
            (c.medicare || 0) + (c.sdi || 0)
          );
          c.totalDeductions = c.totalTaxes + (c.deductions || 0);
        }
        c.netPay = roundToTwo((c.grossPay || 0) - (c.totalDeductions || c.totalTaxes || 0));
      }
    }

    // 4. Cascade: scale the SAME field proportionally on all older stubs
    //    so the YTD chain still ties out.  Formula: for each older
    //    stub i+1, its new YTD for `field` = (edited YTD on current
    //    stub) − (sum of CURRENT `field` values from stub i down to
    //    stub stubIndex).  This matches how the gross cascade works.
    if (!clearing) {
      const targetYtd = overrides[field] || 0;
      let running = targetYtd;
      for (let i = stubIndex; i < newStubs.length - 1; i++) {
        const curField = CURRENT_FIELD_MAP[field];
        const curVal = (newStubs[i].current?.[curField]) || 0;
        const priorTarget = roundToTwo(running - curVal);
        if (priorTarget < 0) break;
        const priorOvrs = { ...(newStubs[i + 1].ytdOverrides || {}) };
        priorOvrs[field] = priorTarget;
        newStubs[i + 1].ytdOverrides = priorOvrs;
        const priorStubNum = stubNumberInYear - (i + 1);
        newStubs[i + 1].ytd = buildStubYtd(
          newStubs[i + 1].current,
          priorStubNum,
          seed(priorStubNum, 'bp-field'),
          newStubs[i + 1].ytdOverride,
          newStubs[i + 1].ytdOverrides,
        );
        running = priorTarget;
      }
    } else {
      // Clearing — strip this field override from every older stub so
      // the cascade chain unwinds too.
      for (let i = stubIndex + 1; i < newStubs.length; i++) {
        if (newStubs[i].ytdOverrides && field in newStubs[i].ytdOverrides) {
          const ovrs = { ...newStubs[i].ytdOverrides };
          delete ovrs[field];
          newStubs[i].ytdOverrides = ovrs;
          const sNum = stubNumberInYear - i;
          newStubs[i].ytd = buildStubYtd(
            newStubs[i].current, sNum, seed(sNum, 'clear-field'),
            newStubs[i].ytdOverride,
            newStubs[i].ytdOverrides,
          );
        }
      }
    }

    updateFormData({ paystubs: newStubs });
  };

  // Clear every YTD override on every stub — Edit YTD "Reset" button.
  const clearAllYtdOverrides = () => {
    const newStubs = [...formData.paystubs];
    const stubNumberInYear = parseInt(formData.stubNumberInYear) || 1;
    const seed = (n, tag = '') =>
      `${formData.employeeName || 'emp'}|${formData.employeeSSN || ''}|${n}|${tag}`;
    for (let i = 0; i < newStubs.length; i++) {
      newStubs[i].ytdOverrides = {};
      newStubs[i].ytdOverride = null;
      const sNum = stubNumberInYear - i;
      newStubs[i].ytd = buildStubYtd(
        newStubs[i].current, sNum, seed(sNum, 'reset'),
        null, null,
      );
    }
    updateFormData({ paystubs: newStubs });
    toast.success('All YTD overrides cleared — reverted to auto-calculated.');
  };

  const updateStubField = (stubIndex, field, value) => {
    const newStubs = [...formData.paystubs];
    const numValue = field.includes('Date') || field === 'checkNumber' || field === 'periodStart' || field === 'periodEnd' || field === 'payDate'
      ? value
      : parseFloat(value) || 0;

    if (field.startsWith('current.')) {
      const fieldName = field.replace('current.', '');
      newStubs[stubIndex].current[fieldName] = typeof numValue === 'number' ? roundToTwo(numValue) : numValue;
      
      // If hours changed, recalculate regular pay based on rate
      if (fieldName === 'hours') {
        const rate = newStubs[stubIndex].current.rate || formData.hourlyRate || 20;
        newStubs[stubIndex].current.regular = roundToTwo(numValue * rate);
      }
      
      // If rate changed, recalculate regular pay based on hours
      if (fieldName === 'rate') {
        const hours = newStubs[stubIndex].current.hours || 0;
        newStubs[stubIndex].current.regular = roundToTwo(hours * numValue);
      }
    } else if (['periodStart', 'periodEnd', 'payDate', 'checkNumber'].includes(field)) {
      newStubs[stubIndex][field] = value;

      if (field === 'payDate' && value && stubIndex === 0) {
        autoCalculateAllDates(newStubs, value);
      }
    }

    const current = newStubs[stubIndex].current;
    const TAX_FIELDS = ['federalTax', 'stateTax', 'socialSecurity', 'medicare', 'sdi'];
    const editedField = field.startsWith('current.') ? field.replace('current.', '') : null;
    if (TAX_FIELDS.includes(editedField)) {
      newStubs[stubIndex].taxOverrides = { ...(newStubs[stubIndex].taxOverrides || {}), [editedField]: true };
    }
    const taxOverrides = newStubs[stubIndex].taxOverrides || {};

    // Recalculate gross pay from all earnings
    current.grossPay = roundToTwo(
      (current.regular || 0) + (current.overtime || 0) +
      (current.bonus || 0) + (current.commission || 0) + (current.tips || 0) +
      (current.holidayPay || 0) + (current.sickPay || 0) + (current.vacationPay || 0)
    );

    // Recalculate taxes based on new gross pay
    const multiplier = getTaxMultiplier();
    const baseTaxes = calculatePayPeriodTaxes(
      current.grossPay,
      formData.payFrequency,
      formData.taxState,
      formData.maritalStatus || 'single',
      formData.exemptions || 1,
      current.grossPay * stubIndex
    );

    // Typed tax amounts are kept; only untouched taxes follow the new gross.
    if (!taxOverrides.federalTax) current.federalTax = roundToTwo(baseTaxes.federalTax * multiplier);
    if (!taxOverrides.stateTax) current.stateTax = roundToTwo(baseTaxes.stateTax * multiplier);
    if (!taxOverrides.socialSecurity) current.socialSecurity = roundToTwo(baseTaxes.socialSecurity);
    if (!taxOverrides.medicare) current.medicare = roundToTwo(baseTaxes.medicare);
    if (!taxOverrides.sdi) current.sdi = roundToTwo(baseTaxes.sdi * multiplier);

    current.totalTaxes = roundToTwo(
      (current.federalTax || 0) + (current.stateTax || 0) +
      (current.socialSecurity || 0) + (current.medicare || 0) + (current.sdi || 0)
    );
    current.totalDeductions = current.totalTaxes + (current.deductions || 0);
    current.netPay = roundToTwo(current.grossPay - current.totalDeductions);

    // Update YTD for all stubs from the changed one onwards
    for (let i = stubIndex; i < newStubs.length; i++) {
      const actualStubNum = stubNumberInYear - i;
      // 2026-02-XX — buildStubYtd honours the per-stub ytdOverride
      // the user may have typed in the YTD input, so edited YTD values
      // don't get wiped away when the user tweaks a current-period
      // field afterwards.
      newStubs[i].ytd = buildStubYtd(
        newStubs[i].current,
        actualStubNum,
        `${formData.employeeName || 'emp'}|${formData.employeeSSN || ''}|${actualStubNum}|edit`,
        newStubs[i].ytdOverride,
        newStubs[i].ytdOverrides,
      );
      newStubs[i].stubNumber = actualStubNum;
      newStubs[i].stubNumberInYear = actualStubNum;
    }

    updateFormData({ paystubs: newStubs });
  };

  const autoCalculateAllDates = (stubs, latestPayDate) => {
    const days = PERIOD_DAYS[formData.payFrequency] || 14;
    const [year, month, day] = latestPayDate.split('-').map(Number);
    // Use local noon so DST + toISOString drift can never bump a day.
    const baseDate = new Date(year, month - 1, day, 12, 0, 0);

    for (let i = 0; i < stubs.length; i++) {
      // 2026-02-28 — use the shared backdate helper so monthly steps
      // back by ONE CALENDAR MONTH (not a flat 30 days) — this keeps
      // the day-of-month stable (e.g. Aug 21 → Jul 21 → Jun 21).
      const payDateObj = backdatePayDate(baseDate, i, formData.payFrequency);
      const periodEndObj = new Date(payDateObj);
      periodEndObj.setDate(periodEndObj.getDate() - 5);
      const periodStartObj = new Date(periodEndObj);
      periodStartObj.setDate(periodStartObj.getDate() - (days - 1));

      stubs[i].payDate = formatLocalDate(payDateObj);
      stubs[i].periodEnd = formatLocalDate(periodEndObj);
      stubs[i].periodStart = formatLocalDate(periodStartObj);
    }

    toast.success(`Auto-calculated dates for ${stubs.length} paystubs based on ${formData.payFrequency} schedule`);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return (amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getEffectiveTaxRate = () => {
    if (!formData.paystubs || formData.paystubs.length === 0) return 0;
    const stub = formData.paystubs[0];
    if (!stub?.current?.grossPay || stub.current.grossPay === 0) return 0;
    return ((stub.current.totalTaxes / stub.current.grossPay) * 100).toFixed(1);
  };

  const payFrequencyLabel = {
    'weekly': 'Weekly',
    'bi-weekly': 'Bi-Weekly',
    'semi-monthly': 'Semi-Monthly',
    'monthly': 'Monthly'
  }[formData.payFrequency] || 'Bi-Weekly';

  // 2026-02-XX (user request) — Shared renderer for every YTD cell in
  // the Earnings / Deductions tables.  When Edit YTD mode is OFF,
  // behaves exactly like the old display (right-aligned formatted
  // currency).  When ON, swaps in a compact inline input so the user
  // can type a new value.  `field` must be a key `updateYtdFieldOverride`
  // knows how to cascade.
  const renderYtdCell = (stub, index, field, extraCls = '') => {
    const val = stub.ytd?.[field];
    if (!editYtdMode) {
      return <span className={extraCls}>{formatCurrency(val)}</span>;
    }
    const isEdited = stub.ytdOverrides && Object.prototype.hasOwnProperty.call(stub.ytdOverrides, field);
    return (
      <div className="flex items-center justify-end gap-1">
        <span className="text-zinc-500 text-[10px]">$</span>
        <Input
          type="number"
          step="0.01"
          min="0"
          placeholder={formatCurrency(val)}
          value={isEdited ? stub.ytdOverrides[field] : ''}
          onChange={(e) => updateYtdFieldOverride(index, field, e.target.value)}
          className={`w-24 h-7 text-xs text-right px-1 bg-zinc-800/80 border ${isEdited ? 'border-amber-500/70 text-amber-200' : 'border-zinc-700 text-zinc-300'}`}
          data-testid={`ytd-field-${field}-${index}`}
          title={`YTD ${field} — type a new value to override, clear to revert`}
        />
      </div>
    );
  };

  return (
    <div ref={stepRootRef} className="space-y-6">
      {/* TARGET NET PAY MODE - KEY FEATURE */}
      <Card className="bg-gradient-to-br from-zinc-900 to-zinc-800 border-zinc-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-white flex items-center gap-3">
            <Target className="w-5 h-5 text-green-400" />
            <div className="flex flex-col">
              <span>Reverse Calculation</span>
              <span className="text-xs text-zinc-400 font-normal mt-1">
                Applies to <span className="text-amber-300 font-semibold">ALL paystubs</span> · For one stub only, expand that stub&apos;s card and use its own Reverse Calculator
              </span>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Mode selector buttons */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => {
                setTargetNetPayMode(true);
                setTargetGrossPayMode(false);
              }}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                targetNetPayMode
                  ? 'bg-green-600 text-white'
                  : 'text-zinc-400 hover:text-white'
              }`}
              data-testid="target-net-pay-btn"
            >
              Target Net
            </button>
            <button
              onClick={() => {
                setTargetGrossPayMode(true);
                setTargetNetPayMode(false);
              }}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                targetGrossPayMode
                  ? 'bg-blue-600 text-white'
                  : 'text-zinc-400 hover:text-white'
              }`}
              data-testid="target-gross-pay-btn"
            >
              Target Gross
            </button>
            <button
              onClick={() => {
                setTargetNetPayMode(false);
                setTargetGrossPayMode(false);
              }}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                !targetNetPayMode && !targetGrossPayMode
                  ? 'bg-zinc-600 text-white'
                  : 'text-zinc-400 hover:text-white'
              }`}
              data-testid="target-off-btn"
            >
              Off
            </button>
          </div>

          {/* TARGET NET PAY MODE */}
          {targetNetPayMode && (
            <div className="space-y-4">
              <p className="text-sm text-green-300 bg-green-500/10 p-3 rounded-lg">
                <strong>TARGET NET PAY:</strong> Enter your desired check amount (net pay) below. The system will automatically calculate Gross Pay, Hours (if hourly), and all Taxes to match this exact amount.
              </p>
              
              {/* Adjustment Field Selector */}
              {includeExtraIncome && (
                <div className="space-y-2">
                  <Label className="text-zinc-300 text-sm">Where to add extra income:</Label>
                  <div className="flex flex-wrap gap-2">
                    {['bonus', 'commission', 'tips', 'holidayPay', 'sickPay', 'vacationPay'].map((field) => (
                      <button
                        key={field}
                        onClick={() => setAdjustmentField(field)}
                        className={`py-1 px-3 rounded text-xs font-medium transition-all ${
                          adjustmentField === field
                            ? 'bg-amber-600 text-white'
                            : 'bg-zinc-800 border border-zinc-600 text-zinc-400 hover:border-zinc-500'
                        }`}
                      >
                        {field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1')}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <Label className="text-zinc-300">Target Net Pay (Check Amount)</Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                    <Input
                      type="number"
                      step="0.01"
                      value={targetNetPay}
                      onChange={(e) => setTargetNetPay(e.target.value)}
                      className="pl-8 bg-zinc-800 border-green-500/50 text-white text-xl h-12"
                      data-testid="target-net-pay-input"
                    />
                  </div>
                </div>
                <Button
                  onClick={() => calculateFromTargetNetPay(parseFloat(targetNetPay))}
                  className="bg-green-600 hover:bg-green-700 h-12 px-6"
                  data-testid="calculate-reverse-btn"
                >
                  <Calculator className="w-4 h-4 mr-2" />
                  Calculate
                </Button>
              </div>
            </div>
          )}

          {/* TARGET GROSS PAY MODE */}
          {targetGrossPayMode && (
            <div className="space-y-4">
              <p className="text-sm text-blue-300 bg-blue-500/10 p-3 rounded-lg">
                <strong>TARGET GROSS PAY:</strong> Enter your desired gross pay amount below. The system will automatically calculate Hours (if hourly) to match this amount, then calculate all Taxes.
              </p>

              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <Label className="text-zinc-300">Target Gross Pay</Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                    <Input
                      type="number"
                      step="0.01"
                      value={targetGrossPay}
                      onChange={(e) => setTargetGrossPay(e.target.value)}
                      className="pl-8 bg-zinc-800 border-blue-500/50 text-white text-xl h-12"
                      data-testid="target-gross-pay-input"
                    />
                  </div>
                </div>
                <Button
                  onClick={() => calculateFromTargetGrossPay(parseFloat(targetGrossPay))}
                  className="bg-blue-600 hover:bg-blue-700 h-12 px-6"
                  data-testid="calculate-gross-btn"
                >
                  <Calculator className="w-4 h-4 mr-2" />
                  Calculate
                </Button>
              </div>
            </div>
          )}

          {/* OFF MODE */}
          {!targetNetPayMode && !targetGrossPayMode && (
            <p className="text-sm text-zinc-400">
              Enable <strong>Target Net</strong> to enter your desired check amount and have the system calculate backwards, or <strong>Target Gross</strong> to set gross pay and calculate hours.
            </p>
          )}
        </CardContent>
      </Card>

      {/* 2026-02-28 (user request) — Deductions Meter (replaces the old
          Tax Withholding Intensity slider).  5-20% range in 1% steps.
          Slider value = target effective deduction rate (deductions /
          gross).  Real per-state tax tables still drive the breakdown
          — the meter just scales the overall intensity. */}
      <Card className="bg-zinc-900 border-zinc-700">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between mb-3">
            <Label className="text-zinc-300 flex items-center gap-2">
              <Sliders className="w-4 h-4 text-amber-400" />
              Deductions Meter
              <span className="text-[10px] uppercase tracking-wide text-zinc-500 ml-1">
                5% – 20% · applies to all stubs
              </span>
            </Label>
            <button
              onClick={() => {
                recalculateAllTaxes();
                toast.success('Deductions recalculated using real state tax rates');
              }}
              className="flex items-center gap-1 px-3 py-1 bg-zinc-700 hover:bg-zinc-600 rounded text-xs text-zinc-300 transition-colors"
              data-testid="refresh-calculations-btn"
            >
              <RefreshCw className="w-3 h-3" />
              Recalculate
            </button>
          </div>

          {/* Preset percentages inside the 5-20% range */}
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="text-xs text-zinc-500">Presets:</span>
            {[5, 8, 10, 12, 15, 18, 20].map((pct) => (
              <button
                key={pct}
                onClick={() => {
                  setTaxIntensityPct(pct);
                  setTimeout(() => recalculateAllTaxes(), 100);
                }}
                className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                  taxIntensityPct === pct
                    ? 'bg-amber-500 text-black'
                    : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                }`}
                data-testid={`deductions-preset-${pct}`}
              >
                {pct}%
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3 text-xs text-zinc-500 mb-2">
            <span>5% · Low deductions</span>
            <span className="font-bold text-amber-400 text-lg">{taxIntensityPct}%</span>
            <span>20% · High deductions</span>
          </div>
          <Slider
            value={[taxIntensityPct]}
            onValueChange={(vals) => setTaxIntensityPct(vals[0])}
            min={5}
            max={20}
            step={1}
            className="w-full"
            data-testid="deductions-meter-slider"
          />
          <div className="flex justify-between text-xs text-zinc-500 mt-2">
            <span>Slide right to add more deductions</span>
            <span className="font-semibold text-amber-400">Live effective rate: ~{getEffectiveTaxRate()}%</span>
            <span>Per-state tax tables honoured</span>
          </div>
        </CardContent>
      </Card>

      {/* Pay Frequency & Date Controls */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <Label className="text-zinc-300 text-sm">Pay Frequency</Label>
          <Badge className="mt-1 bg-zinc-800 text-white">{payFrequencyLabel}</Badge>
        </div>

        <div>
          <Label className="text-zinc-300 text-sm">Year</Label>
          <Select
            value={String(selectedYear)}
            onValueChange={(value) => {
              setSelectedYear(parseInt(value));
              updateFormData({ selectedYear: parseInt(value) });
              setTimeout(() => generateInitialPaystubs(), 100);
            }}
          >
            <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-600 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AVAILABLE_YEARS.map((year) => (
                <SelectItem key={year} value={String(year)}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-zinc-300 text-sm flex items-center gap-1">
            Stub # in Year
            <Badge variant="outline" className="text-xs">(AUTO)</Badge>
          </Label>
          <Input
            type="number"
            min={1}
            max={PERIODS_PER_YEAR[formData.payFrequency] || 26}
            value={stubNumberInYear}
            onChange={(e) => {
              const val = parseInt(e.target.value) || 1;
              setStubNumberInYear(val);
            }}
            className="mt-1 bg-zinc-800 border-green-500/50 text-white text-center"
            data-testid="stub-number-in-year-input"
          />
          <p className="text-xs text-zinc-500 mt-1">
            of {PERIODS_PER_YEAR[formData.payFrequency] || 26}
          </p>
        </div>

        <div>
          <Label className="text-zinc-300 text-sm">First Pay Period Start</Label>
          <Input
            type="date"
            value={formData.periodStartDate || ''}
            onChange={(e) => {
              updateFormData({ periodStartDate: e.target.value });
              setTimeout(() => generateInitialPaystubs(), 100);
            }}
            className="mt-1 bg-zinc-800 border-zinc-600 text-white"
          />
        </div>
      </div>

      {/* Reset Button */}
      <Button
        variant="outline"
        onClick={() => {
          if (window.confirm('This will reset ALL paystub values to defaults. Are you sure?')) {
            generateInitialPaystubs();
            toast.success('All paystubs reset to defaults');
          }
        }}
        className="border-red-600/50 text-red-400 hover:text-red-300 hover:bg-red-900/20"
      >
        <RefreshCw className="w-4 h-4 mr-2" />
        Reset All to Defaults
      </Button>

      {/* 2026-06-23 — Prior Pay Periods YTD editor.  Lets the user dial in
          per-period earnings for every period BEFORE the earliest stub
          being generated, then roll them up into the YTD column with a
          single click. */}
      <PriorPeriodsEditor formData={formData} updateFormData={updateFormData} />

      {/* 2026-02-XX (user request "streamlined + Apply") — LIVE TOTALS PREVIEW.
          Shows the final numbers for EVERY stub (Current Gross, Current Net,
          YTD Gross, YTD Net) in real time.  As the user edits anything above,
          these numbers update instantly — so by the time they land on the
          Preview step they already know exactly what the PDF will print.
          The "Recalculate All" button forces a full re-sync pass and shows
          a toast confirming every stub committed its latest edits. */}
      <div className="sticky top-2 z-20 rounded-xl border border-emerald-500/30 bg-zinc-950/95 backdrop-blur-md shadow-xl p-3" data-testid="live-totals-preview">
        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <h4 className="text-sm font-semibold text-emerald-200">Live Totals — this is exactly what the PDF will print</h4>
          </div>
          <Button
            size="sm"
            onClick={() => {
              // Force a full rebuild pass so every stub's ytd/current block
              // reflects the latest state in formData.paystubs.
              const stubNumberInYearVal = parseInt(formData.stubNumberInYear) || 1;
              const seed = (n, tag = '') =>
                `${formData.employeeName || 'emp'}|${formData.employeeSSN || ''}|${n}|${tag}`;
              const refreshed = (formData.paystubs || []).map((s, i) => {
                const n = stubNumberInYearVal - i;
                return {
                  ...s,
                  ytd: buildStubYtd(s.current, n, seed(n, 'recalc'), s.ytdOverride, s.ytdOverrides || {}),
                };
              });
              updateFormData({ paystubs: refreshed });
              toast.success('All numbers recalculated — PDF will print these exact amounts');
            }}
            className="h-8 bg-emerald-600 hover:bg-emerald-500 text-white text-xs"
            data-testid="recalculate-all-btn"
          >
            <RefreshCw className="w-3 h-3 mr-1" /> Recalculate All
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-zinc-500 border-b border-zinc-800">
                <th className="text-left py-1 pr-2 font-medium">Stub</th>
                <th className="text-right py-1 px-2 font-medium">Current Gross</th>
                <th className="text-right py-1 px-2 font-medium">Current Net</th>
                <th className="text-right py-1 px-2 font-medium text-emerald-300">YTD Gross</th>
                <th className="text-right py-1 px-2 font-medium text-emerald-300">YTD Net</th>
                <th className="text-right py-1 pl-2 font-medium text-amber-300">Edits</th>
              </tr>
            </thead>
            <tbody>
              {(formData.paystubs || []).map((s, idx) => {
                const stubNum = (parseInt(formData.stubNumberInYear) || 1) - idx;
                const edits = (s.ytdOverrides ? Object.keys(s.ytdOverrides).length : 0) + (s.ytdOverride ? 1 : 0);
                return (
                  <tr key={idx} className="border-b border-zinc-800/60 last:border-0" data-testid={`live-totals-row-${idx}`}>
                    <td className="py-1 pr-2 text-zinc-300">#{stubNum}</td>
                    <td className="py-1 px-2 text-right text-white">${formatCurrency(s.current?.grossPay)}</td>
                    <td className="py-1 px-2 text-right text-white">${formatCurrency(s.current?.netPay)}</td>
                    <td className="py-1 px-2 text-right text-emerald-300 font-semibold">${formatCurrency(s.ytd?.grossPay)}</td>
                    <td className="py-1 px-2 text-right text-emerald-300 font-semibold">${formatCurrency(s.ytd?.netPay)}</td>
                    <td className="py-1 pl-2 text-right">
                      {edits > 0
                        ? <span className="text-amber-300 text-[10px] bg-amber-500/15 border border-amber-500/40 rounded px-1.5 py-0.5">{edits} edit{edits > 1 ? 's' : ''}</span>
                        : <span className="text-zinc-600 text-[10px]">auto</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* 2026-02-28 (user request) — Simplified YTD edits strip.
          YTD cells are editable BY DEFAULT (no toggle gate) because the
          admin always wants to tweak them.  This slim banner only shows
          when the user has made at least one override — then a "Clear
          edits" button appears.  Removes the big orange banner the
          previous version placed above the stub list. */}
      {(() => {
        const overrideCount = (formData.paystubs || []).reduce((n, s) => {
          if (s?.ytdOverride != null && s.ytdOverride !== '') n += 1;
          if (s?.ytdOverrides) n += Object.keys(s.ytdOverrides).length;
          return n;
        }, 0);
        if (overrideCount === 0) {
          return (
            <div className="text-[11px] text-zinc-500 flex items-center gap-2"
                 data-testid="edit-ytd-mode-hint">
              <Edit3 className="w-3 h-3" />
              <span>Every YTD cell is editable — tap any amount to override.</span>
            </div>
          );
        }
        return (
          <div className="rounded-lg bg-amber-500/10 border border-amber-500/40 p-2.5 flex items-center justify-between gap-3 text-xs"
               data-testid="edit-ytd-mode-banner">
            <div className="flex items-center gap-2 text-amber-200">
              <Edit3 className="w-3.5 h-3.5" />
              <span>
                <span className="font-semibold">{overrideCount}</span> YTD edit{overrideCount === 1 ? '' : 's'} active ·
                prior stubs back-fill proportionally so totals stay consistent.
              </span>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={clearAllYtdOverrides}
              className="h-7 border-amber-500/40 text-amber-200 hover:bg-amber-500/15 text-[11px]"
              data-testid="clear-all-ytd-overrides-btn"
            >
              <RefreshCw className="w-3 h-3 mr-1" /> Clear edits
            </Button>
          </div>
        );
      })()}

      {/* Paystub Cards */}
      {formData.paystubs?.map((stub, index) => (
        <Card 
          key={index} 
          className="bg-zinc-900 border-zinc-700"
        >
          <div 
            className="p-4 cursor-pointer"
            onClick={() => setExpandedStub(expandedStub === index ? -1 : index)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Badge className="bg-amber-600 text-white">
                  #{stub.stubNumberInYear || stub.stubNumber}
                </Badge>
                <span className="text-zinc-400 text-sm">
                  Pay Period {stub.stubNumberInYear || stubNumberInYear - index} of {selectedYear}
                </span>
                <Badge variant="outline" className="text-zinc-400 border-zinc-600">
                  Check #{stub.checkNumber}
                </Badge>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-zinc-400 text-sm">
                  {formatDate(stub.periodStart)} - {formatDate(stub.periodEnd)} | Pay: {formatDate(stub.payDate)}
                </span>
                <div className="text-right">
                  <p className="text-xs text-zinc-500">Net Pay</p>
                  <p className="text-lg font-bold text-green-400">${formatCurrency(stub.current?.netPay)}</p>
                </div>
                {expandedStub === index ? (
                  <ChevronUp className="w-5 h-5 text-zinc-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-zinc-400" />
                )}
              </div>
            </div>
          </div>

          {expandedStub === index && stub.current && (
            <CardContent className="border-t border-zinc-700 pt-4">
              {/* Editable Dates Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div>
                  <Label className="text-zinc-400 text-xs">Check Number</Label>
                  <Input
                    value={stub.checkNumber}
                    onChange={(e) => updateStubField(index, 'checkNumber', e.target.value)}
                    className="bg-zinc-800 border-zinc-600 text-white text-sm"
                    data-testid={`check-number-${index}`}
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs">Period Start</Label>
                  <Input
                    type="date"
                    value={stub.periodStart}
                    onChange={(e) => updateStubField(index, 'periodStart', e.target.value)}
                    className="bg-zinc-800 border-zinc-600 text-white text-sm"
                    data-testid={`period-start-${index}`}
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs">Period End</Label>
                  <Input
                    type="date"
                    value={stub.periodEnd}
                    onChange={(e) => updateStubField(index, 'periodEnd', e.target.value)}
                    className="bg-zinc-800 border-zinc-600 text-white text-sm"
                    data-testid={`period-end-${index}`}
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs flex items-center gap-1">
                    Pay Date {index === 0 && <Badge className="bg-amber-500 text-xs">AUTO-CALC</Badge>}
                  </Label>
                  <Input
                    type="date"
                    value={stub.payDate}
                    onChange={(e) => updateStubField(index, 'payDate', e.target.value)}
                    className={`text-white text-sm ${index === 0
                      ? 'bg-amber-900/30 border-amber-500 ring-1 ring-amber-500/50'
                      : 'bg-zinc-800 border-zinc-600'}`}
                    data-testid={`pay-date-${index}`}
                  />
                  {index === 0 && (
                    <p className="text-xs text-amber-400 mt-1">
                      Change this to auto-calculate all dates
                    </p>
                  )}
                </div>
              </div>

              {/* 2026-06-23 — Per-stub Reverse Calculator.  Lets the user
                  punch a target net or gross JUST for this paystub without
                  touching the others. Calls the existing reverse-calc
                  helpers with stubIndex=index so only this card updates. */}
              <div className="mt-4 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3" data-testid={`per-stub-reverse-${index}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Target className="w-4 h-4 text-emerald-300" />
                  <span className="text-sm font-semibold text-emerald-300">
                    Reverse calculator · Stub #{stub.stubNumberInYear || stub.stubNumber} only
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div className="flex gap-1.5">
                    <div className="relative flex-1">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400 text-xs">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Target NET (this stub)"
                        value={stubTargetNet[index] ?? ''}
                        onChange={(e) => setStubTargetNet((p) => ({ ...p, [index]: e.target.value }))}
                        className="h-9 pl-6 bg-zinc-900 border-emerald-500/40 text-white text-sm"
                        data-testid={`stub-${index}-target-net-input`}
                      />
                    </div>
                    <Button
                      size="sm"
                      onClick={() => {
                        const v = parseFloat(stubTargetNet[index]);
                        if (!v || v <= 0) {
                          toast.error('Enter a Target Net amount first');
                          return;
                        }
                        const ex = stubExtras[index];
                        calculateFromTargetNetPay(v, index, ex?.enabled ? ex : null);
                      }}
                      className="h-9 bg-emerald-600 hover:bg-emerald-500 text-white text-xs whitespace-nowrap"
                      data-testid={`stub-${index}-apply-net-btn`}
                    >
                      <Calculator className="w-3 h-3 mr-1" /> Apply Net
                    </Button>
                  </div>
                  <div className="flex gap-1.5">
                    <div className="relative flex-1">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400 text-xs">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Target GROSS (this stub)"
                        value={stubTargetGross[index] ?? ''}
                        onChange={(e) => setStubTargetGross((p) => ({ ...p, [index]: e.target.value }))}
                        className="h-9 pl-6 bg-zinc-900 border-blue-500/40 text-white text-sm"
                        data-testid={`stub-${index}-target-gross-input`}
                      />
                    </div>
                    <Button
                      size="sm"
                      onClick={() => {
                        const v = parseFloat(stubTargetGross[index]);
                        if (!v || v <= 0) {
                          toast.error('Enter a Target Gross amount first');
                          return;
                        }
                        const ex = stubExtras[index];
                        calculateFromTargetGrossPay(v, index, ex?.enabled ? ex : null);
                      }}
                      className="h-9 bg-blue-600 hover:bg-blue-500 text-white text-xs whitespace-nowrap"
                      data-testid={`stub-${index}-apply-gross-btn`}
                    >
                      <Calculator className="w-3 h-3 mr-1" /> Apply Gross
                    </Button>
                  </div>
                </div>
                {/* 2026-02-28 (user request) — Simplify.  The "Add the
                    extra to" picker + "Target includes extras" block
                    are advanced controls most users never touch.
                    Collapsed behind a lightweight "Advanced options"
                    disclosure so the Reverse Calculator reads as just
                    two inputs + two buttons by default. */}
                {(() => {
                  const _advKey = `adv-${index}`;
                  const _open = !!stubExtras?.[`__adv_${index}`];
                  const _toggleAdv = () => setStubExtras((p) => ({
                    ...p,
                    [`__adv_${index}`]: !_open,
                  }));
                  return (
                    <>
                      <button
                        type="button"
                        onClick={_toggleAdv}
                        className="mt-2 text-[11px] text-emerald-300 hover:text-emerald-200 flex items-center gap-1"
                        data-testid={`stub-${index}-reverse-adv-toggle`}
                        aria-expanded={_open}
                        aria-controls={_advKey}
                      >
                        {_open ? '− Hide' : '+ Show'} advanced options
                        <span className="text-zinc-500 ml-1">
                          (extras breakdown · where the extra goes)
                        </span>
                      </button>
                      {_open && (
                        <div id={_advKey} className="mt-2 space-y-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <label className="text-[11px] text-zinc-400">Add the extra to:</label>
                            <select
                              value={stubAdjustmentFields[index] || adjustmentField || 'bonus'}
                              onChange={(e) => {
                                const next = { ...stubAdjustmentFields, [index]: e.target.value };
                                setStubAdjustmentFields(next);
                                updateFormData({ stubAdjustmentFields: next });
                              }}
                              className="h-7 px-2 rounded bg-zinc-900 border border-zinc-700 text-white text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
                              data-testid={`stub-${index}-adj-field`}
                            >
                              <option value="bonus">Bonus</option>
                              <option value="commission">Commission</option>
                              <option value="tips">Tips</option>
                              <option value="holidayPay">Holiday Pay</option>
                              <option value="sickPay">Sick Pay</option>
                              <option value="vacationPay">Vacation Pay</option>
                            </select>
                          </div>

                          {(() => {
                            const ex = stubExtras[index] || { enabled: false, overtime: '', bonus: '', commission: '', tips: '' };
                            const setEx = (patch) => setStubExtras((p) => ({ ...p, [index]: { ...ex, ...patch } }));
                            return (
                              <div className="rounded-md border border-amber-500/20 bg-amber-500/5 p-2" data-testid={`stub-${index}-extras-picker`}>
                                <label className="flex items-center gap-2 cursor-pointer text-xs text-zinc-300">
                                  <input
                                    type="checkbox"
                                    checked={!!ex.enabled}
                                    onChange={(e) => setEx({ enabled: e.target.checked })}
                                    className="accent-amber-500"
                                    data-testid={`stub-${index}-extras-enable`}
                                  />
                                  <span>Target amount <span className="text-amber-300 font-semibold">includes</span> extras (overtime, bonus, commission, tips)?</span>
                                </label>
                                {ex.enabled && (
                                  <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                                    {[
                                      { k: 'overtime', label: 'Overtime', ring: 'border-orange-500/40 text-orange-300' },
                                      { k: 'bonus', label: 'Bonus', ring: 'border-green-500/40 text-green-300' },
                                      { k: 'commission', label: 'Commission', ring: 'border-cyan-500/40 text-cyan-300' },
                                      { k: 'tips', label: 'Tips', ring: 'border-amber-500/40 text-amber-300' },
                                    ].map(({ k, label, ring }) => (
                                      <div key={k} className="flex flex-col">
                                        <label className="text-[10px] uppercase tracking-wide text-zinc-500 mb-0.5">{label}</label>
                                        <div className="relative">
                                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500 text-xs">$</span>
                                          <Input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            placeholder="0.00"
                                            value={ex[k] ?? ''}
                                            onChange={(e) => setEx({ [k]: e.target.value })}
                                            className={`h-8 pl-6 bg-zinc-900 border text-xs ${ring}`}
                                            data-testid={`stub-${index}-extra-${k}`}
                                          />
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {ex.enabled && (
                                  <p className="text-[10px] text-amber-300/80 mt-1.5">
                                    Regular pay will be back-solved so Regular + Extras = your target.
                                  </p>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </>
                  );
                })()}

                {/* Recipe panel — visible after a calc; shows the user the
                    exact breakdown so they know what numbers were used. */}
                {stub.current && stub.current.grossPay > 0 && (() => {
                  const c = stub.current || {};
                  const reg = Number(c.regular || 0);
                  const ot = Number(c.overtime || 0);
                  const bonus = Number(c.bonus || 0);
                  const commission = Number(c.commission || 0);
                  const tips = Number(c.tips || 0);
                  const extras = bonus + commission + tips + Number(c.holidayPay || 0) + Number(c.sickPay || 0) + Number(c.vacationPay || 0);
                  if (extras <= 0 && ot <= 0) return null;
                  return (
                    <div className="mt-3 rounded-md bg-zinc-900 border border-emerald-500/30 p-3 text-xs" data-testid={`stub-${index}-recipe`}>
                      <p className="text-emerald-300 font-semibold mb-1.5">📋 How this stub hits ${Number(c.netPay || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} net:</p>
                      <ul className="space-y-0.5 text-zinc-300 ml-1">
                        <li>• Regular pay: <span className="text-white font-mono">${reg.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>{c.rate ? <span className="text-zinc-500"> (${Number(c.rate).toFixed(2)}/hr × {c.hours || 0} hrs)</span> : null}</li>
                        {ot > 0 && (<li>• Overtime (1.5×): <span className="text-orange-300 font-mono">${ot.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></li>)}
                        {bonus > 0 && (<li>• Bonus: <span className="text-amber-300 font-mono">${bonus.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></li>)}
                        {commission > 0 && (<li>• Commission: <span className="text-amber-300 font-mono">${commission.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></li>)}
                        {tips > 0 && (<li>• Tips: <span className="text-amber-300 font-mono">${tips.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></li>)}
                        <li className="pt-1 border-t border-zinc-800">= Gross: <span className="text-white font-mono">${Number(c.grossPay || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span> − Taxes <span className="text-rose-300 font-mono">${Number(c.totalTaxes || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span> = <span className="text-emerald-300 font-mono">${Number(c.netPay || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></li>
                      </ul>
                      <p className="text-[10px] text-zinc-500 mt-1.5">💡 Tip: change &quot;Add the extra to&quot; above and re-apply to shift the extra into Overtime, Tips, Commission, etc.</p>
                    </div>
                  );
                })()}

                <p className="text-[11px] text-zinc-500 mt-2">
                  Type a number and tap <span className="text-emerald-300 font-semibold">Apply</span>. Only this stub will be recalculated — the others stay as-is.
                </p>
              </div>

              {/* TWO COLUMN LAYOUT */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* LEFT: EARNINGS */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-bold text-white flex items-center gap-2">
                      <Banknote className="w-4 h-4 text-green-400" />
                      EARNINGS
                    </h4>
                    {/* 2026-02-28 (user request) — Per-stub Recalculate.
                        Rebuilds the stub's Current + YTD from the current
                        edits so sum of earnings + taxes + net all match. */}
                    <button
                      onClick={() => {
                        try {
                          recalculateAllTaxes();
                          toast.success(`Stub #${stub.stubNumberInYear || stub.stubNumber} recalculated`);
                        } catch (e) { toast.error('Recalc failed'); }
                      }}
                      className="flex items-center gap-1 px-2 py-1 bg-green-500/10 border border-green-500/40 hover:bg-green-500/20 rounded text-[11px] text-green-300 transition-colors"
                      data-testid={`stub-${index}-recalc-earnings`}
                    >
                      <RefreshCw className="w-3 h-3" />
                      Recalculate
                    </button>
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-zinc-700 text-zinc-400">
                        <th className="text-left py-2">Description</th>
                        <th className="text-right py-2">Current</th>
                        <th className="text-right py-2">YTD</th>
                      </tr>
                    </thead>
                    <tbody className="text-white">
                      {/* Hourly Rate Row — editable inline (Feb 2026) */}
                      {formData.paymentType === 'hourly' && (
                        <tr className="border-b border-zinc-800">
                          <td className="py-2 text-zinc-400">Hourly Rate</td>
                          <td className="py-2 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <span className="text-zinc-500 text-xs">$</span>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={stub.current.rate ?? (formData.hourlyRate || 20)}
                                onChange={(e) => updateStubField(index, 'current.rate', e.target.value)}
                                className="w-24 bg-zinc-800 border-cyan-500/50 text-cyan-300 text-right text-sm h-8"
                                data-testid={`paystub-hourly-rate-${index}`}
                              />
                              <span className="text-zinc-500 text-xs">/hr</span>
                            </div>
                          </td>
                          <td className="py-2 text-right text-zinc-500 text-xs">
                            {formatCurrency((stub.current.rate ?? formData.hourlyRate ?? 20) * (stub.ytd?.hours || 0))}
                          </td>
                        </tr>
                      )}
                      {/* Hours Row */}
                      {formData.paymentType === 'hourly' && (
                        <tr className="border-b border-zinc-800">
                          <td className="py-2 text-zinc-400">Hours Worked</td>
                          <td className="py-2 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => {
                                  const newHours = Math.max(0, (stub.current.hours || 80) - 1);
                                  updateStubField(index, 'current.hours', newHours);
                                }}
                                className="px-2 py-1 bg-zinc-700 hover:bg-zinc-600 rounded text-white text-xs"
                              >
                                -
                              </button>
                              <Input
                                type="number"
                                value={stub.current.hours || 0}
                                onChange={(e) => updateStubField(index, 'current.hours', e.target.value)}
                                className="w-16 bg-zinc-800 border-blue-500/50 text-blue-300 text-center text-sm h-8"
                              />
                              <button
                                onClick={() => {
                                  const newHours = Math.min(168, (stub.current.hours || 80) + 1);
                                  updateStubField(index, 'current.hours', newHours);
                                }}
                                className="px-2 py-1 bg-zinc-700 hover:bg-zinc-600 rounded text-white text-xs"
                              >
                                +
                              </button>
                            </div>
                          </td>
                          <td className="py-2 text-right text-zinc-400">{formatCurrency(stub.ytd?.hours)} hrs</td>
                        </tr>
                      )}
                      <tr className="border-b border-zinc-800">
                        <td className="py-2">Regular</td>
                        <td className="py-2">
                          <Input
                            type="number"
                            step="0.01"
                            value={stub.current.regular || 0}
                            onChange={(e) => updateStubField(index, 'current.regular', e.target.value)}
                            className="w-full bg-zinc-800 border-zinc-600 text-white text-right text-sm h-8"
                          />
                        </td>
                        <td className="py-2 text-right">{renderYtdCell(stub, index, 'regular')}</td>
                      </tr>
                      <tr className="border-b border-zinc-800">
                        <td className="py-2 text-orange-300">Overtime (1.5x)</td>
                        <td className="py-2">
                          <Input
                            type="number"
                            step="0.01"
                            value={stub.current.overtime || 0}
                            onChange={(e) => updateStubField(index, 'current.overtime', e.target.value)}
                            className="w-full bg-zinc-800 border-orange-500/50 text-orange-300 text-right text-sm h-8"
                          />
                        </td>
                        <td className="py-2 text-right text-orange-300">{renderYtdCell(stub, index, 'overtime', 'text-orange-300')}</td>
                      </tr>
                      <tr className="border-b border-zinc-800">
                        <td className="py-2 text-green-300">Bonus</td>
                        <td className="py-2">
                          <Input
                            type="number"
                            step="0.01"
                            value={stub.current.bonus || 0}
                            onChange={(e) => updateStubField(index, 'current.bonus', e.target.value)}
                            className="w-full bg-zinc-800 border-green-500/50 text-green-300 text-right text-sm h-8"
                          />
                        </td>
                        <td className="py-2 text-right text-green-300">{renderYtdCell(stub, index, 'bonus', 'text-green-300')}</td>
                      </tr>
                      <tr className="border-b border-zinc-800">
                        <td className="py-2">Commission</td>
                        <td className="py-2">
                          <Input
                            type="number"
                            step="0.01"
                            value={stub.current.commission || 0}
                            onChange={(e) => updateStubField(index, 'current.commission', e.target.value)}
                            className="w-full bg-zinc-800 border-zinc-600 text-white text-right text-sm h-8"
                          />
                        </td>
                        <td className="py-2 text-right">{renderYtdCell(stub, index, 'commission')}</td>
                      </tr>
                      <tr className="border-b border-zinc-800">
                        <td className="py-2">Tips</td>
                        <td className="py-2">
                          <Input
                            type="number"
                            step="0.01"
                            value={stub.current.tips || 0}
                            onChange={(e) => updateStubField(index, 'current.tips', e.target.value)}
                            className="w-full bg-zinc-800 border-zinc-600 text-white text-right text-sm h-8"
                          />
                        </td>
                        <td className="py-2 text-right">{renderYtdCell(stub, index, 'tips')}</td>
                      </tr>
                      <tr className="border-t-2 border-zinc-600 font-bold">
                        <td className="py-2">Gross Pay</td>
                        <td className="py-2 text-right text-green-400">${formatCurrency(stub.current.grossPay)}</td>
                        <td className="py-2 text-right text-green-400">{editYtdMode ? renderYtdCell(stub, index, 'grossPay', 'text-green-400') : <>${formatCurrency(stub.ytd?.grossPay)}</>}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* RIGHT: DEDUCTIONS */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-bold text-white flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-400" />
                      DEDUCTIONS
                    </h4>
                    <button
                      onClick={() => {
                        try {
                          recalculateAllTaxes();
                          toast.success(`Stub #${stub.stubNumberInYear || stub.stubNumber} deductions recalculated`);
                        } catch (e) { toast.error('Recalc failed'); }
                      }}
                      className="flex items-center gap-1 px-2 py-1 bg-red-500/10 border border-red-500/40 hover:bg-red-500/20 rounded text-[11px] text-red-300 transition-colors"
                      data-testid={`stub-${index}-recalc-deductions`}
                    >
                      <RefreshCw className="w-3 h-3" />
                      Recalculate
                    </button>
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-zinc-700 text-zinc-400">
                        <th className="text-left py-2">Description</th>
                        <th className="text-right py-2">Current</th>
                        <th className="text-right py-2">YTD</th>
                      </tr>
                    </thead>
                    <tbody className="text-white">
                      <tr className="border-b border-zinc-800">
                        <td className="py-2">FICA - Medicare</td>
                        <td className="py-2">
                          <Input
                            type="number"
                            step="0.01"
                            value={stub.current.medicare || 0}
                            onChange={(e) => updateStubField(index, 'current.medicare', e.target.value)}
                            className="w-full bg-zinc-800 border-zinc-600 text-white text-right text-sm h-8"
                          />
                        </td>
                        <td className="py-2 text-right">{renderYtdCell(stub, index, 'medicare')}</td>
                      </tr>
                      <tr className="border-b border-zinc-800">
                        <td className="py-2">FICA - Social Security</td>
                        <td className="py-2">
                          <Input
                            type="number"
                            step="0.01"
                            value={stub.current.socialSecurity || 0}
                            onChange={(e) => updateStubField(index, 'current.socialSecurity', e.target.value)}
                            className="w-full bg-zinc-800 border-zinc-600 text-white text-right text-sm h-8"
                          />
                        </td>
                        <td className="py-2 text-right">{renderYtdCell(stub, index, 'socialSecurity')}</td>
                      </tr>
                      <tr className="border-b border-zinc-800">
                        <td className="py-2">Federal Tax</td>
                        <td className="py-2">
                          <Input
                            type="number"
                            step="0.01"
                            value={stub.current.federalTax || 0}
                            onChange={(e) => updateStubField(index, 'current.federalTax', e.target.value)}
                            className="w-full bg-zinc-800 border-zinc-600 text-white text-right text-sm h-8"
                          />
                        </td>
                        <td className="py-2 text-right">{renderYtdCell(stub, index, 'federalTax')}</td>
                      </tr>
                      <tr className="border-b border-zinc-800">
                        <td className="py-2">State Tax</td>
                        <td className="py-2">
                          <Input
                            type="number"
                            step="0.01"
                            value={stub.current.stateTax || 0}
                            onChange={(e) => updateStubField(index, 'current.stateTax', e.target.value)}
                            className="w-full bg-zinc-800 border-zinc-600 text-white text-right text-sm h-8"
                          />
                        </td>
                        <td className="py-2 text-right">{renderYtdCell(stub, index, 'stateTax')}</td>
                      </tr>
                      <tr className="border-b border-zinc-800">
                        <td className="py-2">SDI</td>
                        <td className="py-2">
                          <Input
                            type="number"
                            step="0.01"
                            value={stub.current.sdi || 0}
                            onChange={(e) => updateStubField(index, 'current.sdi', e.target.value)}
                            className="w-full bg-zinc-800 border-zinc-600 text-white text-right text-sm h-8"
                          />
                        </td>
                        <td className="py-2 text-right">{renderYtdCell(stub, index, 'sdi')}</td>
                      </tr>
                      <tr className="border-t-2 border-zinc-600 font-bold text-red-400">
                        <td className="py-2">Total Deduction</td>
                        <td className="py-2 text-right">${formatCurrency(stub.current.totalTaxes)}</td>
                        <td className="py-2 text-right">{editYtdMode ? renderYtdCell(stub, index, 'totalTaxes') : <>${formatCurrency(stub.ytd?.totalTaxes)}</>}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* NET PAY SUMMARY */}
              <div className="mt-6 p-4 bg-gradient-to-r from-green-900/20 to-zinc-900 rounded-lg border border-green-500/30">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-xs text-zinc-400">Gross Pay</p>
                    <p className="text-lg font-bold text-white">${formatCurrency(stub.current.grossPay)}</p>
                    {/* 2026-02-XX (user request) — Editable YTD Gross.
                        Typing an override here saves it to this stub
                        AND back-propagates proportionally to every
                        prior stub so the whole YTD column adds up
                        correctly.  Clear the field to revert to the
                        automatic jittered calculation. */}
                    <div className="mt-1 flex items-center justify-center gap-1">
                      <span className="text-xs text-zinc-500">YTD:</span>
                      <span className="text-xs text-zinc-500">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder={formatCurrency(stub.ytd?.grossPay)}
                        value={stub.ytdOverride ?? ''}
                        onChange={(e) => updateYtdOverride(index, e.target.value)}
                        className={`w-28 h-6 text-xs text-right bg-zinc-800/80 border px-1 ${stub.ytdOverride ? 'border-yellow-500/60 text-yellow-200' : 'border-zinc-700 text-zinc-300'}`}
                        data-testid={`ytd-override-${index}`}
                        title="Type a YTD gross to override the auto-calculated value. Previous stubs back-fill proportionally. Clear the field to revert."
                      />
                    </div>
                    {stub.ytdOverride ? (
                      <p className="text-[10px] text-yellow-400/80 mt-0.5">Edited · prior stubs back-filled</p>
                    ) : null}
                  </div>
                  <div>
                    <p className="text-xs text-zinc-400">Deductions</p>
                    <p className="text-lg font-bold text-red-400">-${formatCurrency(stub.current.totalTaxes)}</p>
                    {editYtdMode ? (
                      <div className="mt-1 flex items-center justify-center gap-1">
                        <span className="text-xs text-zinc-500">YTD:</span>
                        <span className="text-xs text-zinc-500">$</span>
                        <Input
                          type="number" step="0.01" min="0"
                          placeholder={formatCurrency(stub.ytd?.totalTaxes)}
                          value={stub.ytdOverrides?.totalTaxes ?? ''}
                          onChange={(e) => updateYtdFieldOverride(index, 'totalTaxes', e.target.value)}
                          className={`w-24 h-6 text-xs text-right bg-zinc-800/80 border px-1 ${stub.ytdOverrides?.totalTaxes != null ? 'border-amber-500/70 text-amber-200' : 'border-zinc-700 text-zinc-300'}`}
                          data-testid={`ytd-field-totalTaxes-summary-${index}`}
                        />
                      </div>
                    ) : (
                      <p className="text-xs text-zinc-500">YTD: ${formatCurrency(stub.ytd?.totalTaxes)}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-zinc-400">Net Pay</p>
                    <p className="text-2xl font-bold text-green-400">${formatCurrency(stub.current.netPay)}</p>
                    {editYtdMode ? (
                      <div className="mt-1 flex items-center justify-center gap-1">
                        <span className="text-xs text-green-300">YTD:</span>
                        <span className="text-xs text-green-300">$</span>
                        <Input
                          type="number" step="0.01" min="0"
                          placeholder={formatCurrency(stub.ytd?.netPay)}
                          value={stub.ytdOverrides?.netPay ?? ''}
                          onChange={(e) => updateYtdFieldOverride(index, 'netPay', e.target.value)}
                          className={`w-24 h-6 text-xs text-right bg-zinc-800/80 border px-1 ${stub.ytdOverrides?.netPay != null ? 'border-amber-500/70 text-amber-200' : 'border-zinc-700 text-green-300'}`}
                          data-testid={`ytd-field-netPay-${index}`}
                        />
                      </div>
                    ) : (
                      <p className="text-xs text-green-300">YTD: ${formatCurrency(stub.ytd?.netPay)}</p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          )}
        </Card>
      ))}

      {/* Action Buttons */}
      <div className="flex gap-4">
        <Button
          variant="outline"
          onClick={generateInitialPaystubs}
          className="border-zinc-600 text-zinc-400 hover:text-white"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Regenerate All
        </Button>
        {onSaveClient && (
          <Button
            onClick={onSaveClient}
            className="bg-green-600 hover:bg-green-700 text-white"
            data-testid="save-client-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            Save Progress
          </Button>
        )}
      </div>
    </div>
  );
}
